/**
 * Renaissance faires finder content.
 *
 * GENERATED from the claude.ai/design handoff "Renaissance Faires Finder"
 * (project de71ee8e, file `Renaissance Faires Finder.dc.html` + its
 * `faires-data.js` mirror of `renaissance-faires-master.md`). Venue names,
 * street addresses, coordinates and official URLs come from the handoff's
 * `renaissance-faires-geodata.md`; dates were verified per faire against the
 * faire's own site (`dateConfidence: 'official'`) or a directory listing
 * (`'secondary'`), with `'unposted'` rows carrying a month-precision window
 * rather than a guessed day. Nearest-city trip links point at active Wanderu
 * bus-tickets pages.
 *
 * Accuracy is this page's whole competitive wedge, so the counts below are
 * derived from the array rather than written down, and `data.test.ts` pins the
 * shape. Re-verify the list each season and bump `LAST_VERIFIED`.
 *
 * Nine rows were corrected against the handoff on import, each caught by
 * `data.test.ts`:
 * - Three Barons (AK) and the Original Renaissance Pleasure Faire (CA) carried
 *   "largest in AK" / "oldest in the US (since 1963)" inside `datesText`. Both
 *   are ranking claims in a dates field; the Pleasure Faire's 1963 provenance
 *   is stated in {@link flagshipNotes} and the FAQ, where it belongs.
 * - End of the Road (AK) was graded `unposted` while carrying firm Jun 19–20
 *   dates. Regraded `secondary`: a date shown next to a "not posted yet" badge
 *   contradicts itself, and we can't claim we read these off the faire's site.
 * - Five `nearestCity` labels abbreviated a city its own `nearestCityShort`
 *   (and so the trip button beside it) spelled out — "NYC" vs "New York",
 *   "NYC/Newark" vs "Newark", "LA/Simi Valley" vs "Los Angeles".
 * - Mutton & Mead (MA) linked `http://`; that site serves https. Two others
 *   genuinely have no working TLS and keep their `http://` links.
 */
import type { Faire, FaqItem, Tip } from './types'

/** Date the whole list was last re-verified. Rendered on the page verbatim. */
export const LAST_VERIFIED = '2026-08-10'

/** Human form of {@link LAST_VERIFIED} — a fixed string, never a clock read. */
export const LAST_VERIFIED_LABEL = 'August 10, 2026'

export const faires: Faire[] = [
    {
        id: 'the-maine-renaissance-faire-me',
        name: 'The Maine Renaissance Faire',
        city: 'Acton',
        state: 'ME',
        stateName: 'Maine',
        region: 'Northeast',
        lat: 43.5014,
        lng: -70.9014,
        venue: 'Acton Fairgrounds',
        address: '550 ME-109, Acton, ME 04001',
        url: 'https://themainerenfaire.com/',
        datesText: 'Jul 18–19, 25–26, Aug 1–2',
        start: '2026-07-18',
        end: '2026-08-02',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Portland (Amtrak Downeaster, Concord Coach)',
        nearestCityShort: 'Portland',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-me/portland/',
    },
    {
        id: 'new-hampshire-renaissance-faire-nh',
        name: 'New Hampshire Renaissance Faire',
        city: 'Fremont',
        state: 'NH',
        stateName: 'New Hampshire',
        region: 'Northeast',
        lat: 42.9908,
        lng: -71.1425,
        venue: 'NHRF grounds',
        address: '80 Martin Rd, Fremont, NH 03044',
        url: 'https://nhrenfaire.com/',
        datesText: 'May 9–10, 16–17',
        start: '2026-05-09',
        end: '2026-05-17',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Exeter (Amtrak Downeaster); Boston',
        nearestCityShort: 'Boston',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ma/boston/',
    },
    {
        id: 'franklin-renaissance-faire-nh',
        name: 'Franklin Renaissance Faire',
        city: 'Franklin',
        state: 'NH',
        stateName: 'New Hampshire',
        region: 'Northeast',
        lat: 43.446,
        lng: -71.6504,
        venue: 'Odell Park',
        address: '124-125 Memorial St, Franklin, NH 03235',
        url: 'https://www.franklinnh.gov/home/events/14503',
        datesText: 'Sep 12–13',
        start: '2026-09-12',
        end: '2026-09-13',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Concord (Concord Coach)',
        nearestCityShort: 'Concord',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nh/concord/',
    },
    {
        id: 'the-vagabonds-faire-nh',
        name: "The Vagabond's Faire",
        city: 'Dover',
        state: 'NH',
        stateName: 'New Hampshire',
        region: 'Northeast',
        lat: 43.1956,
        lng: -70.8734,
        venue: 'Henry Law Park',
        address: '28 Henry Law Ave, Dover, NH 03820',
        url: 'https://www.vagabondsfaire.com/',
        datesText: 'Jun 20 (one night)',
        start: '2026-06-20',
        end: '2026-06-20',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Dover (Amtrak Downeaster, C&J)',
        nearestCityShort: 'Dover',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nh/dover/',
    },
    {
        id: 'vermont-renaissance-faire-vt',
        name: 'Vermont Renaissance Faire',
        city: 'Essex Junction',
        state: 'VT',
        stateName: 'Vermont',
        region: 'Northeast',
        lat: 44.4906,
        lng: -73.1068,
        venue: 'Champlain Valley Exposition',
        address: '105 Pearl St, Essex Junction, VT 05452',
        url: 'https://www.vtgatherings.com/vtrennfaire.html',
        datesText: 'Jun 27–28',
        start: '2026-06-27',
        end: '2026-06-28',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Burlington (Amtrak Vermonter)',
        nearestCityShort: 'Burlington',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-vt/burlington/',
    },
    {
        id: 'vermont-winter-renaissance-faire-vt',
        name: 'Vermont Winter Renaissance Faire',
        city: 'Essex Junction',
        state: 'VT',
        stateName: 'Vermont',
        region: 'Northeast',
        lat: 44.4906,
        lng: -73.1068,
        venue: 'Champlain Valley Exposition',
        address: '105 Pearl St, Essex Junction, VT 05452',
        url: 'https://www.vtgatherings.com/winter-renaissance-faire.html',
        datesText: 'Feb 7–8',
        start: '2026-02-07',
        end: '2026-02-08',
        seasons: ['winter'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Burlington (Amtrak Vermonter)',
        nearestCityShort: 'Burlington',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-vt/burlington/',
    },
    {
        id: 'the-grammar-school-medieval-faire-vt',
        name: 'The Grammar School Medieval Faire',
        city: 'Putney',
        state: 'VT',
        stateName: 'Vermont',
        region: 'Northeast',
        lat: 42.986,
        lng: -72.518,
        venue: 'The Grammar School',
        address: '69 Hickory Ridge Rd S, Putney, VT 05346',
        url: 'https://www.thegrammarschool.org/medieval-faire',
        datesText: 'Oct 17',
        start: '2026-10-17',
        end: '2026-10-17',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Brattleboro (Amtrak Vermonter, Greyhound)',
        nearestCityShort: 'Brattleboro',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-vt/brattleboro/',
    },
    {
        id: 'king-richards-faire-ma',
        name: "King Richard's Faire",
        city: 'Carver',
        state: 'MA',
        stateName: 'Massachusetts',
        region: 'Northeast',
        lat: 41.84594,
        lng: -70.77278,
        venue: 'Edaville site',
        address: '5 Pine St, Carver, MA 02330',
        url: 'https://www.kingrichardsfaire.net/',
        datesText: 'Sep 5 – Oct 25 (+ Mon Sep 7, Oct 12)',
        start: '2026-09-05',
        end: '2026-10-25',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Boston (Plymouth & Brockton, Peter Pan)',
        nearestCityShort: 'Boston',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ma/boston/',
    },
    {
        id: 'autumn-knights-renaissance-faire-ma',
        name: 'Autumn Knights Renaissance Faire',
        city: 'Charlton',
        state: 'MA',
        stateName: 'Massachusetts',
        region: 'Northeast',
        lat: 42.163,
        lng: -71.958,
        venue: 'Autumn Knights grounds',
        address: '120 Northside Rd, Charlton, MA 01507',
        url: 'https://autumnknightsfaire.com/',
        datesText: 'Oct 23–24, 30–31',
        start: '2026-10-23',
        end: '2026-10-31',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Worcester (Peter Pan, MBTA/Amtrak)',
        nearestCityShort: 'Worcester',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ma/worcester/',
    },
    {
        id: 'massachusetts-renaissance-faire-ma',
        name: 'Massachusetts Renaissance Faire',
        city: 'Cummington',
        state: 'MA',
        stateName: 'Massachusetts',
        region: 'Northeast',
        lat: 42.46,
        lng: -72.896,
        venue: 'Cummington Fairgrounds',
        address: '97 Fairgrounds Rd, Cummington, MA 01026',
        url: 'https://massrenfaire.com/',
        datesText: 'None posted — possible hiatus',
        start: null,
        end: null,
        seasons: ['summer'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Northampton (Peter Pan)',
        nearestCityShort: 'Northampton',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ma/northampton/',
    },
    {
        id: 'mutton-and-mead-medieval-festival-ma',
        name: 'Mutton & Mead Medieval Festival',
        city: 'Montague',
        state: 'MA',
        stateName: 'Massachusetts',
        region: 'Northeast',
        lat: 42.585,
        lng: -72.53,
        venue: 'Millers Falls Rod & Gun Club',
        address: '210 Turners Falls Rd, Montague, MA 01351',
        url: 'https://www.muttonandmead.org/',
        datesText: 'On hiatus',
        start: null,
        end: null,
        seasons: ['summer'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Greenfield/Northampton (Peter Pan)',
        nearestCityShort: 'Northampton',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ma/northampton/',
    },
    {
        id: 'ocean-state-renaissance-faire-ri',
        name: 'Ocean State Renaissance Faire',
        city: 'Wakefield',
        state: 'RI',
        stateName: 'Rhode Island',
        region: 'Northeast',
        lat: 41.443,
        lng: -71.501,
        venue: 'Saugatucket Park',
        address: '101 High St, Wakefield, RI 02879',
        url: 'https://www.osrenfaire.com/',
        datesText: 'Aug 28–30',
        start: '2026-08-28',
        end: '2026-08-30',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Kingston (Amtrak, ~4 mi); Providence',
        nearestCityShort: 'Kingston',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ri/kingston/',
    },
    {
        id: 'connecticut-renaissance-faire-ct',
        name: 'Connecticut Renaissance Faire',
        city: 'Lebanon',
        state: 'CT',
        stateName: 'Connecticut',
        region: 'Northeast',
        lat: 41.64181,
        lng: -72.23784,
        venue: 'Lebanon Country Fairgrounds',
        address: '122 Mack Rd, Lebanon, CT 06249',
        url: 'https://www.ctfaire.com/',
        datesText: 'Aug 29 – Oct 25 (+ Mon Sep 7, Oct 12)',
        start: '2026-08-29',
        end: '2026-10-25',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'New London (Amtrak); Hartford (Peter Pan)',
        nearestCityShort: 'New London',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ct/new-london/',
    },
    {
        id: 'midsummer-fantasy-renaissance-faire-ct',
        name: 'Midsummer Fantasy Renaissance Faire',
        city: 'Ansonia',
        state: 'CT',
        stateName: 'Connecticut',
        region: 'Northeast',
        lat: 41.352,
        lng: -73.055,
        venue: 'Warsaw Park',
        address: '119 Pulaski Hwy, Ansonia, CT 06401',
        url: 'https://www.mfrenfaire.com/',
        datesText: 'Jun 27–28, Jul 4–5, 11–12',
        start: '2026-06-27',
        end: '2026-07-12',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'New Haven (Amtrak/Metro-North)',
        nearestCityShort: 'New Haven',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ct/new-haven/',
    },
    {
        id: 'robin-hoods-medieval-faire-ct',
        name: "Robin Hood's Medieval Faire",
        city: 'Harwinton',
        state: 'CT',
        stateName: 'Connecticut',
        region: 'Northeast',
        lat: 41.76227,
        lng: -73.04596,
        venue: 'Harwinton Fairgrounds',
        address: '150 Locust Rd, Harwinton, CT 06791',
        url: 'https://robinhoodsfaire.com/',
        datesText: 'May 16 – Jun 21',
        start: '2026-05-16',
        end: '2026-06-21',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Waterbury/Hartford (Peter Pan)',
        nearestCityShort: 'Hartford',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ct/hartford/',
    },
    {
        id: 'torrington-renaissance-faire-ct',
        name: 'Torrington Renaissance Faire',
        city: 'Torrington',
        state: 'CT',
        stateName: 'Connecticut',
        region: 'Northeast',
        lat: 41.847,
        lng: -73.135,
        venue: 'YMCA Camp M.O.E.',
        address: '1145 Brandy Hill Rd, Torrington, CT 06790',
        url: null,
        datesText: 'Apr 25',
        start: '2026-04-25',
        end: '2026-04-25',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Waterbury/Hartford',
        nearestCityShort: 'Hartford',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ct/hartford/',
    },
    {
        id: 'new-york-renaissance-faire-ny',
        name: 'New York Renaissance Faire',
        city: 'Tuxedo',
        state: 'NY',
        stateName: 'New York',
        region: 'Northeast',
        lat: 41.1977,
        lng: -74.2587,
        venue: 'Sterling Forest',
        address: '600 Route 17A, Tuxedo, NY 10987',
        url: 'https://www.renfair.com/ny/',
        datesText: 'Aug 15 – Oct 4 (+ Labor Day)',
        start: '2026-08-15',
        end: '2026-10-04',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'New York (Metro-North Port Jervis line, Coach USA)',
        nearestCityShort: 'New York',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ny/new-york/',
    },
    {
        id: 'sterling-renaissance-festival-ny',
        name: 'Sterling Renaissance Festival',
        city: 'Sterling',
        state: 'NY',
        stateName: 'New York',
        region: 'Northeast',
        lat: 43.36148,
        lng: -76.65287,
        venue: 'Sterling Ren Fest grounds',
        address: '15385 Farden Road, Sterling, NY 13156',
        url: 'https://www.sterlingfestival.com/',
        datesText: 'Jul 4 – Aug 16 (50th)',
        start: '2026-07-04',
        end: '2026-08-16',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Syracuse (Amtrak/Trailways)',
        nearestCityShort: 'Syracuse',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ny/syracuse/',
    },
    {
        id: 'hawk-creek-wildlife-and-renaissance-festival-ny',
        name: 'Hawk Creek Wildlife & Renaissance Festival',
        city: 'West Falls',
        state: 'NY',
        stateName: 'New York',
        region: 'Northeast',
        lat: 42.715,
        lng: -78.667,
        venue: 'Hawk Creek Wildlife Center',
        address: '1963 Mill Road, West Falls, NY 14170',
        url: 'https://www.hawkcreek.org/',
        datesText: 'July (weekend TBD)',
        start: '2026-07',
        end: null,
        seasons: ['summer'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Buffalo',
        nearestCityShort: 'Buffalo',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ny/buffalo/',
    },
    {
        id: 'medieval-festival-at-fort-tryon-park-ny',
        name: 'Medieval Festival at Fort Tryon Park',
        city: 'New York',
        state: 'NY',
        stateName: 'New York',
        region: 'Northeast',
        lat: 40.8623,
        lng: -73.933,
        venue: 'Fort Tryon Park',
        address: '99 Margaret Corbin Dr, New York, NY 10040',
        url: 'https://www.forttryonparktrust.org/event/medieval-festival/',
        datesText: 'Late Sep / early Oct (TBA)',
        start: '2026-09',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'New York (in the city; free)',
        nearestCityShort: 'New York',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ny/new-york/',
    },
    {
        id: 'new-jersey-renaissance-faire-nj',
        name: 'New Jersey Renaissance Faire',
        city: 'Columbus',
        state: 'NJ',
        stateName: 'New Jersey',
        region: 'Northeast',
        lat: 40.0692,
        lng: -74.688,
        venue: 'Burlington County Fairgrounds',
        address: '1990 Jacksonville Jobstown Rd, Columbus, NJ 08022',
        url: 'https://njrenfaire.com/',
        datesText: 'May 23–24, 30–31, Jun 6–7, 13–14',
        start: '2026-05-23',
        end: '2026-06-14',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Trenton (Amtrak/NJ Transit); Philadelphia',
        nearestCityShort: 'Trenton',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nj/trenton/',
    },
    {
        id: 'renaissance-faire-at-historic-smithville-nj',
        name: 'Renaissance Faire at Historic Smithville',
        city: 'Absecon',
        state: 'NJ',
        stateName: 'New Jersey',
        region: 'Northeast',
        lat: 39.4934,
        lng: -74.4562,
        venue: 'Historic Smithville & Village Greene',
        address: '615 E Moss Mill Rd, Smithville, NJ 08205',
        url: 'https://www.historicsmithville.com/',
        datesText: 'Apr 11–12',
        start: '2026-04-11',
        end: '2026-04-12',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Atlantic City (NJ Transit)',
        nearestCityShort: 'Atlantic City',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nj/atlantic-city/',
    },
    {
        id: 'sparta-nj-renaissance-festival-nj',
        name: 'Sparta NJ Renaissance Festival',
        city: 'Sparta',
        state: 'NJ',
        stateName: 'New Jersey',
        region: 'Northeast',
        lat: 41.045,
        lng: -74.64,
        venue: 'Camp Sacajawea',
        address: '844 White Lake Road, Sparta, NJ 07871',
        url: 'https://spartanjrenfaire.com/',
        datesText: 'Sep 5–6, 12–13, 19–20',
        start: '2026-09-05',
        end: '2026-09-20',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Newark',
        nearestCityShort: 'Newark',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nj/newark/',
    },
    {
        id: 'cape-may-renaissance-faire-nj',
        name: 'Cape May Renaissance Faire',
        city: 'Cape May Court House',
        state: 'NJ',
        stateName: 'New Jersey',
        region: 'Northeast',
        lat: 39.087,
        lng: -74.831,
        venue: 'Cape May County 4-H Fairgrounds',
        address: '355 Court House-South Dennis Rd, Cape May Court House, NJ 08210',
        url: 'https://capemayrenfaire.com/',
        datesText: 'May 2–3',
        start: '2026-05-02',
        end: '2026-05-03',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Atlantic City',
        nearestCityShort: 'Atlantic City',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nj/atlantic-city/',
    },
    {
        id: 'lakewood-renaissance-faire-nj',
        name: 'Lakewood Renaissance Faire',
        city: 'Lakewood Twp',
        state: 'NJ',
        stateName: 'New Jersey',
        region: 'Northeast',
        lat: 40.057,
        lng: -74.238,
        venue: 'Pine Park',
        address: '500 Country Club Dr, Lakewood, NJ 08701',
        url: 'https://lakewoodrenaissancefaire.com/',
        datesText: 'Sep 19–20',
        start: '2026-09-19',
        end: '2026-09-20',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Newark (NJ Transit)',
        nearestCityShort: 'Newark',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nj/newark/',
    },
    {
        id: 'dragon-renaissance-faire-nj',
        name: 'Dragon Renaissance Faire',
        city: 'Bridgeton',
        state: 'NJ',
        stateName: 'New Jersey',
        region: 'Northeast',
        lat: 39.443,
        lng: -75.205,
        venue: 'The Shoppes at Dragon Village',
        address: '97 Trench Rd, Bridgeton, NJ 08302',
        url: 'https://shoppesatdragonvillage.com/',
        datesText: '~June (TBD)',
        start: '2026-06',
        end: null,
        seasons: ['summer'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Philadelphia (or Vineland NJ Transit)',
        nearestCityShort: 'Philadelphia',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-pa/philadelphia/',
    },
    {
        id: 'medfest-drew-university-nj',
        name: 'MedFest (Drew University)',
        city: 'Madison',
        state: 'NJ',
        stateName: 'New Jersey',
        region: 'Northeast',
        lat: 40.762,
        lng: -74.427,
        venue: 'Drew University',
        address: '36 Madison Ave, Madison, NJ 07940',
        url: 'https://groups.drew.edu/tmt',
        datesText: 'Spring (TBD)',
        start: '2026-04',
        end: null,
        seasons: ['spring'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Newark (NJ Transit Morristown line)',
        nearestCityShort: 'Newark',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nj/newark/',
    },
    {
        id: 'pennsylvania-renaissance-faire-pa',
        name: 'Pennsylvania Renaissance Faire',
        city: 'Manheim',
        state: 'PA',
        stateName: 'Pennsylvania',
        region: 'Northeast',
        lat: 40.247,
        lng: -76.444,
        venue: 'Mount Hope Estate & Winery',
        address: '2775 Lebanon Rd, Manheim, PA 17545',
        url: 'https://parenfaire.com/',
        datesText: 'Aug 15 – Oct 25 (13 weekends)',
        start: '2026-08-15',
        end: '2026-10-25',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'Lancaster (Amtrak Keystone)',
        nearestCityShort: 'Lancaster',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-pa/lancaster/',
    },
    {
        id: 'yuletide-at-the-pa-renaissance-faire-pa',
        name: 'Yuletide at the PA Renaissance Faire',
        city: 'Manheim',
        state: 'PA',
        stateName: 'Pennsylvania',
        region: 'Northeast',
        lat: 40.247,
        lng: -76.444,
        venue: 'Mount Hope Estate & Winery',
        address: '2775 Lebanon Rd, Manheim, PA 17545',
        url: 'https://parenfaire.com/',
        datesText: 'Nov 21 – Dec 13',
        start: '2026-11-21',
        end: '2026-12-13',
        seasons: ['winter'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Lancaster (Amtrak Keystone)',
        nearestCityShort: 'Lancaster',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-pa/lancaster/',
    },
    {
        id: 'pittsburgh-renaissance-festival-pa',
        name: 'Pittsburgh Renaissance Festival',
        city: 'West Newton',
        state: 'PA',
        stateName: 'Pennsylvania',
        region: 'Northeast',
        lat: 40.18094,
        lng: -79.69749,
        venue: 'Morelandshire grounds',
        address: '112 Renaissance Lane, West Newton, PA 15089',
        url: 'https://pittsburghrenfest.com/',
        datesText: 'Aug 22 – Sep 27 (+ Labor Day)',
        start: '2026-08-22',
        end: '2026-09-27',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Pittsburgh',
        nearestCityShort: 'Pittsburgh',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-pa/pittsburgh/',
    },
    {
        id: 'philly-faire-pa',
        name: 'Philly Faire',
        city: 'Philadelphia',
        state: 'PA',
        stateName: 'Pennsylvania',
        region: 'Northeast',
        lat: 39.8786,
        lng: -75.2123,
        venue: 'Historic Fort Mifflin',
        address: '6400 Hog Island Rd, Philadelphia, PA 19153',
        url: 'https://phillyfaire.com/',
        datesText: 'May 23–25',
        start: '2026-05-23',
        end: '2026-05-25',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Philadelphia',
        nearestCityShort: 'Philadelphia',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-pa/philadelphia/',
    },
    {
        id: 'bucks-county-renaissance-faire-pa',
        name: 'Bucks County Renaissance Faire',
        city: 'Doylestown',
        state: 'PA',
        stateName: 'Pennsylvania',
        region: 'Northeast',
        lat: 40.299,
        lng: -75.117,
        venue: 'Moravian Pottery & Tile Works',
        address: '130 E Swamp Rd, Doylestown, PA 18901',
        url: 'https://bucksrenfaire.com/',
        datesText: 'Sep 5–7 (Labor Day)',
        start: '2026-09-05',
        end: '2026-09-07',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Philadelphia (SEPTA)',
        nearestCityShort: 'Philadelphia',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-pa/philadelphia/',
    },
    {
        id: 'boalsburg-renaissance-faire-pa',
        name: 'Boalsburg Renaissance Faire',
        city: 'Boalsburg',
        state: 'PA',
        stateName: 'Pennsylvania',
        region: 'Northeast',
        lat: 40.774,
        lng: -77.789,
        venue: 'Boal Mansion Museum',
        address: '163 Boal Estate Dr, Boalsburg, PA 16827',
        url: 'https://www.boalmuseum.com/',
        datesText: '~Oct 11–12',
        start: '2026-10-11',
        end: '2026-10-12',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'State College (Megabus/Fullington)',
        nearestCityShort: 'State College',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-pa/state-college/',
    },
    {
        id: 'gem-city-renaissance-festival-pa',
        name: 'Gem City Renaissance Festival',
        city: 'Erie',
        state: 'PA',
        stateName: 'Pennsylvania',
        region: 'Northeast',
        lat: 42.128,
        lng: -80.094,
        venue: 'Frontier Park',
        address: '1501 W 6th St, Erie, PA 16505',
        url: 'https://gemcityrenaissancefestival.com/',
        datesText: '~Aug 31 (new)',
        start: '2026-08-31',
        end: '2026-08-31',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Erie (Amtrak)',
        nearestCityShort: 'Erie',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-pa/erie/',
    },
    {
        id: 'kutztown-university-renaissance-faire-pa',
        name: 'Kutztown University Renaissance Faire',
        city: 'Kutztown',
        state: 'PA',
        stateName: 'Pennsylvania',
        region: 'Northeast',
        lat: 40.509,
        lng: -75.785,
        venue: 'Kutztown University',
        address: '15200 Kutztown Rd, Kutztown, PA 19530',
        url: 'https://kumrc.weebly.com/',
        datesText: 'Spring (TBD)',
        start: '2026-04',
        end: null,
        seasons: ['spring'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Allentown/Reading',
        nearestCityShort: 'Allentown',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-pa/allentown/',
    },
    {
        id: 'queensland-faire-pa',
        name: 'Queensland Faire',
        city: 'Osterburg',
        state: 'PA',
        stateName: 'Pennsylvania',
        region: 'Northeast',
        lat: 40.199,
        lng: -78.522,
        venue: 'Queensland Faire grounds',
        address: 'Settlers Ridge Rd, Osterburg, PA 16667',
        url: 'https://queenslandfaire.com/',
        datesText: 'TBD',
        start: null,
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Altoona',
        nearestCityShort: 'Altoona',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-pa/altoona/',
    },
    {
        id: 'reading-renfaire-pa',
        name: 'Reading RenFaire',
        city: 'Wernersville',
        state: 'PA',
        stateName: 'Pennsylvania',
        region: 'Northeast',
        lat: 40.335,
        lng: -76.083,
        venue: "St. John's (Hain's) Grove",
        address: '591 N Church Rd, Wernersville, PA 19565',
        url: 'https://opusonechamberchoir.com/renfaire',
        datesText: 'Early June (TBD)',
        start: '2026-06',
        end: null,
        seasons: ['summer'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Reading',
        nearestCityShort: 'Reading',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-pa/reading/',
    },
    {
        id: 'maryland-renaissance-festival-md',
        name: 'Maryland Renaissance Festival',
        city: 'Crownsville',
        state: 'MD',
        stateName: 'Maryland',
        region: 'Mid-Atlantic & Southeast',
        lat: 39.00776,
        lng: -76.60049,
        venue: 'Revel Grove',
        address: '1821 Crownsville Rd, Annapolis, MD 21401',
        url: 'https://www.rennfest.com',
        datesText: 'Aug 29 – Oct 25 (50th season)',
        start: '2026-08-29',
        end: '2026-10-25',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'Baltimore (Amtrak/MARC/Greyhound)',
        nearestCityShort: 'Baltimore',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-md/baltimore/',
    },
    {
        id: 'renfest-southern-maryland-md',
        name: 'RenFest Southern Maryland',
        city: 'Leonardtown',
        state: 'MD',
        stateName: 'Maryland',
        region: 'Mid-Atlantic & Southeast',
        lat: 38.2905,
        lng: -76.639,
        venue: 'Brüdergarten',
        address: '22725 Duke St, Leonardtown, MD 20650',
        url: 'https://renfestsomd.com',
        datesText: 'May 23–24',
        start: '2026-05-23',
        end: '2026-05-24',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Washington DC',
        nearestCityShort: 'Washington',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-dc/washington/',
    },
    {
        id: 'the-delaware-renaissance-faire-de',
        name: 'The Delaware Renaissance Faire',
        city: 'Townsend',
        state: 'DE',
        stateName: 'Delaware',
        region: 'Mid-Atlantic & Southeast',
        lat: 39.315,
        lng: -75.6,
        venue: 'DRF grounds',
        address: '474 Flemings Landing Rd, Townsend, DE 19734',
        url: 'https://delafaire.com',
        datesText: 'Oct 25–26, Nov 1–2, 8–9',
        start: '2026-10-25',
        end: '2026-11-09',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Wilmington (Amtrak)',
        nearestCityShort: 'Wilmington',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-de/wilmington/',
    },
    {
        id: 'blue-hen-renaissance-festival-de',
        name: 'Blue Hen Renaissance Festival',
        city: 'Dover',
        state: 'DE',
        stateName: 'Delaware',
        region: 'Mid-Atlantic & Southeast',
        lat: 39.204,
        lng: -75.589,
        venue: 'Akridge Scout Reservation',
        address: '1910 Baden Powell Way, Dover, DE 19904',
        url: 'https://bluehenrenaissancefestival.com',
        datesText: 'Apr 18–19',
        start: '2026-04-18',
        end: '2026-04-19',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Wilmington (Amtrak); Dover (DART)',
        nearestCityShort: 'Wilmington',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-de/wilmington/',
    },
    {
        id: 'virginia-renaissance-faire-va',
        name: 'Virginia Renaissance Faire',
        city: 'Spotsylvania',
        state: 'VA',
        stateName: 'Virginia',
        region: 'Mid-Atlantic & Southeast',
        lat: 38.0988,
        lng: -77.7476,
        venue: 'Lake Anna Winery',
        address: '5621 Courthouse Rd, Spotsylvania, VA 22551',
        url: 'https://www.varf.org',
        datesText: 'May 9 – Jun 7',
        start: '2026-05-09',
        end: '2026-06-07',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Fredericksburg (Amtrak/VRE)',
        nearestCityShort: 'Fredericksburg',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-va/fredericksburg/',
    },
    {
        id: 'harvest-faire-va',
        name: 'Harvest Faire',
        city: 'Smithfield',
        state: 'VA',
        stateName: 'Virginia',
        region: 'Mid-Atlantic & Southeast',
        lat: 36.967,
        lng: -76.716,
        venue: 'Breezy Hill Meadworks',
        address: '15981 Breezy Hill Ln, Smithfield, VA 23430',
        url: 'https://www.harvestfaireva.org',
        datesText: 'May 2–3 & Sep 5–6',
        start: '2026-09-05',
        end: '2026-09-06',
        seasons: ['spring', 'fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Newport News (Amtrak)',
        nearestCityShort: 'Newport News',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-va/newport-news/',
    },
    {
        id: 'gloucester-renaissance-festival-va',
        name: 'Gloucester Renaissance Festival',
        city: 'Gloucester',
        state: 'VA',
        stateName: 'Virginia',
        region: 'Mid-Atlantic & Southeast',
        lat: 37.4088,
        lng: -76.5222,
        venue: 'Downtown / Court Green',
        address: 'Main St, Gloucester, VA 23061',
        url: null,
        datesText: 'October (TBD)',
        start: '2026-10',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Newport News/Williamsburg',
        nearestCityShort: 'Newport News',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-va/newport-news/',
    },
    {
        id: 'stone-tower-glenn-renaissance-faire-va',
        name: 'Stone Tower Glenn Renaissance Faire',
        city: 'Mt. Solon',
        state: 'VA',
        stateName: 'Virginia',
        region: 'Mid-Atlantic & Southeast',
        lat: 38.3628,
        lng: -79.0873,
        venue: 'Natural Chimneys Park',
        address: '94 Natural Chimneys Ln, Mount Solon, VA 22843',
        url: null,
        datesText: 'June + fall (TBD)',
        start: '2026-09',
        end: null,
        seasons: ['summer', 'fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Staunton (Amtrak); Harrisonburg',
        nearestCityShort: 'Staunton',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-va/staunton/',
    },
    {
        id: 'west-virginia-renaissance-festival-wv',
        name: 'West Virginia Renaissance Festival',
        city: 'Lewisburg',
        state: 'WV',
        stateName: 'West Virginia',
        region: 'Mid-Atlantic & Southeast',
        lat: 37.835,
        lng: -80.35,
        venue: 'Hollow Hills Farm',
        address: '23439 Midland Trail E, Lewisburg, WV 24901',
        url: 'https://wvrenfest.com',
        datesText: 'Jun 6–7, 13–14, 20–21, 27–28',
        start: '2026-06-06',
        end: '2026-06-28',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'White Sulphur Springs (Amtrak Cardinal); Charleston',
        nearestCityShort: 'White Sulphur Springs',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wv/white-sulphur-springs/',
    },
    {
        id: 'carolina-renaissance-festival-nc',
        name: 'Carolina Renaissance Festival',
        city: 'Huntersville',
        state: 'NC',
        stateName: 'North Carolina',
        region: 'Mid-Atlantic & Southeast',
        lat: 35.3736,
        lng: -80.7647,
        venue: 'Carolina Ren Fest grounds',
        address: '16445 Poplar Tent Rd, Huntersville, NC 28078',
        url: 'https://www.royalfaires.com/carolina',
        datesText: 'Oct 3 – Nov 22 (8 weekends)',
        start: '2026-10-03',
        end: '2026-11-22',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'Charlotte (Amtrak/Greyhound)',
        nearestCityShort: 'Charlotte',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nc/charlotte/',
    },
    {
        id: 'eastern-nc-renaissance-faire-nc',
        name: 'Eastern NC Renaissance Faire',
        city: 'Kinston',
        state: 'NC',
        stateName: 'North Carolina',
        region: 'Mid-Atlantic & Southeast',
        lat: 35.283,
        lng: -77.618,
        venue: 'Lenoir County Fairgrounds',
        address: '401 Fairgrounds Rd, Kinston, NC 28504',
        url: 'http://www.encrenfaire.com',
        datesText: 'Apr 11–12',
        start: '2026-04-11',
        end: '2026-04-12',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Raleigh (Amtrak)',
        nearestCityShort: 'Raleigh',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nc/raleigh/',
    },
    {
        id: 'spring-haven-renaissance-festival-nc',
        name: 'Spring Haven Renaissance Festival',
        city: 'Chapel Hill',
        state: 'NC',
        stateName: 'North Carolina',
        region: 'Mid-Atlantic & Southeast',
        lat: 35.965,
        lng: -79.195,
        venue: 'Spring Haven Farm',
        address: '5306 Homer Ruffin Rd, Chapel Hill, NC 27516',
        url: 'https://renfest.farm',
        datesText: 'Apr 11–12 & Nov 14–15',
        start: '2026-11-14',
        end: '2026-11-15',
        seasons: ['spring', 'fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Durham (Amtrak)',
        nearestCityShort: 'Durham',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nc/durham/',
    },
    {
        id: 'fort-bragg-renaissance-faire-nc',
        name: 'Fort Bragg Renaissance Faire',
        city: 'Fayetteville',
        state: 'NC',
        stateName: 'North Carolina',
        region: 'Mid-Atlantic & Southeast',
        lat: 35.148,
        lng: -79.003,
        venue: 'Smith Lake Rec Area (Fort Liberty)',
        address: '1200 Honeycutt Rd, Fort Liberty, NC 28307',
        url: null,
        datesText: 'September (TBD)',
        start: '2026-09',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Fayetteville (Amtrak)',
        nearestCityShort: 'Fayetteville',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nc/fayetteville/',
    },
    {
        id: 'mythical-and-medieval-fest-sc',
        name: 'Mythical & Medieval Fest',
        city: 'Myrtle Beach',
        state: 'SC',
        stateName: 'South Carolina',
        region: 'Mid-Atlantic & Southeast',
        lat: 33.687,
        lng: -78.976,
        venue: 'RH Acres (Socastee)',
        address: '3833 Socastee Blvd, Myrtle Beach, SC 29588',
        url: 'https://mythicalmedievalfest.com',
        datesText: 'Nov 7–8, 14–15',
        start: '2026-11-07',
        end: '2026-11-15',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Florence (Amtrak); Myrtle Beach (Greyhound)',
        nearestCityShort: 'Myrtle Beach',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-sc/myrtle-beach/',
    },
    {
        id: 'upstate-renaissance-faire-sc',
        name: 'Upstate Renaissance Faire',
        city: 'Wellford',
        state: 'SC',
        stateName: 'South Carolina',
        region: 'Mid-Atlantic & Southeast',
        lat: 35.033,
        lng: -82.118,
        venue: 'Hollywild Animal Preserve',
        address: '2325 Hampton Rd, Wellford, SC 29385',
        url: 'https://www.upstaterenaissancefaire.com',
        datesText: 'Fall (TBD)',
        start: '2026-10',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Spartanburg/Greenville (Amtrak)',
        nearestCityShort: 'Greenville',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-sc/greenville/',
    },
    {
        id: 'georgia-renaissance-festival-ga',
        name: 'Georgia Renaissance Festival',
        city: 'Fairburn',
        state: 'GA',
        stateName: 'Georgia',
        region: 'Mid-Atlantic & Southeast',
        lat: 33.533,
        lng: -84.636,
        venue: 'GA Ren Fest grounds',
        address: '6905 Virlyn B. Smith Rd, Fairburn, GA 30213',
        url: 'https://www.garenfest.com',
        datesText: 'Apr 11 – May 31 (+ Memorial Monday)',
        start: '2026-04-11',
        end: '2026-05-31',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'Atlanta (Amtrak/Greyhound/MARTA)',
        nearestCityShort: 'Atlanta',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ga/atlanta/',
    },
    {
        id: 'bay-area-renaissance-festival-fl',
        name: 'Bay Area Renaissance Festival',
        city: 'Dade City',
        state: 'FL',
        stateName: 'Florida',
        region: 'Mid-Atlantic & Southeast',
        lat: 28.32,
        lng: -82.19,
        venue: 'MPI / Auton Rd grounds',
        address: '12838 Auton Rd, Dade City, FL 33525',
        url: 'https://bayarearenfest.com',
        datesText: 'Feb 14 – Mar 29 (48th)',
        start: '2026-02-14',
        end: '2026-03-29',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Tampa (Amtrak/Greyhound)',
        nearestCityShort: 'Tampa',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-fl/tampa/',
    },
    {
        id: 'florida-renaissance-festival-fl',
        name: 'Florida Renaissance Festival',
        city: 'Deerfield Beach',
        state: 'FL',
        stateName: 'Florida',
        region: 'Mid-Atlantic & Southeast',
        lat: 26.305,
        lng: -80.156,
        venue: 'Quiet Waters Park',
        address: '401 S Powerline Rd, Deerfield Beach, FL 33442',
        url: 'https://www.ren-fest.com',
        datesText: 'Feb 7 – Mar 29 (8 weekends)',
        start: '2026-02-07',
        end: '2026-03-29',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Deerfield Beach (Tri-Rail); Ft. Lauderdale (Brightline)',
        nearestCityShort: 'Deerfield Beach',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-fl/deerfield-beach/',
    },
    {
        id: 'sarasota-medieval-fair-fl',
        name: 'Sarasota Medieval Fair',
        city: 'Myakka City',
        state: 'FL',
        stateName: 'Florida',
        region: 'Mid-Atlantic & Southeast',
        lat: 27.365,
        lng: -82.1,
        venue: 'Mallaranny Farms',
        address: '29847 FL-70 E, Myakka City, FL 34251',
        url: 'https://www.sarasotamedievalfair.com',
        datesText: 'Nov 7 – Dec 6 (22nd)',
        start: '2026-11-07',
        end: '2026-12-06',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Tampa (Amtrak)',
        nearestCityShort: 'Tampa',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-fl/tampa/',
    },
    {
        id: 'hoggetowne-medieval-faire-fl',
        name: 'Hoggetowne Medieval Faire',
        city: 'Gainesville',
        state: 'FL',
        stateName: 'Florida',
        region: 'Mid-Atlantic & Southeast',
        lat: 29.647,
        lng: -82.323,
        venue: 'Depot Park',
        address: '874 SE 4th St, Gainesville, FL 32601',
        url: 'https://www.hoggetownemedfaire.com',
        datesText: 'Jan 24–25 (39th; free)',
        start: '2026-01-24',
        end: '2026-01-25',
        seasons: ['winter'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Gainesville (Greyhound); Jacksonville (Amtrak)',
        nearestCityShort: 'Gainesville',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-fl/gainesville/',
    },
    {
        id: 'st-petersburg-renaissance-faire-fl',
        name: 'St. Petersburg Renaissance Faire',
        city: 'Pinellas Park',
        state: 'FL',
        stateName: 'Florida',
        region: 'Mid-Atlantic & Southeast',
        lat: 27.858,
        lng: -82.7,
        venue: 'England Brothers Park',
        address: '5010 81st Ave N, Pinellas Park, FL 33781',
        url: 'https://stpetersburgrenaissancefaire.com',
        datesText: 'Apr 4–26',
        start: '2026-04-04',
        end: '2026-04-26',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'St. Petersburg/Tampa',
        nearestCityShort: 'St. Petersburg',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-fl/st-petersburg/',
    },
    {
        id: 'gulf-coast-renaissance-faire-and-pirate-fest-fl',
        name: 'Gulf Coast Renaissance Faire & Pirate Fest',
        city: 'Pensacola',
        state: 'FL',
        stateName: 'Florida',
        region: 'Mid-Atlantic & Southeast',
        lat: 30.515,
        lng: -87.315,
        venue: 'Five Flags Speedway',
        address: '7451 Pine Forest Rd, Pensacola, FL 32526',
        url: 'https://gcrf.us',
        datesText: 'Feb 28 – Mar 8',
        start: '2026-02-28',
        end: '2026-03-08',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Pensacola (Amtrak/Greyhound)',
        nearestCityShort: 'Pensacola',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-fl/pensacola/',
    },
    {
        id: 'orlando-renaissance-festival-fl',
        name: 'Orlando Renaissance Festival',
        city: 'Orlando',
        state: 'FL',
        stateName: 'Florida',
        region: 'Mid-Atlantic & Southeast',
        lat: 28.383,
        lng: -81.348,
        venue: 'Southport Community Park',
        address: '3505 E Landstreet Rd, Orlando, FL 32827',
        url: 'https://orlandorenaissancefestival.com',
        datesText: 'Dec 5–6, 12–13, 19–20',
        start: '2026-12-05',
        end: '2026-12-20',
        seasons: ['winter'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Orlando/Kissimmee (Amtrak/Brightline)',
        nearestCityShort: 'Orlando',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-fl/orlando/',
    },
    {
        id: 'space-coast-renaissance-fair-fl',
        name: 'Space Coast Renaissance Fair',
        city: 'Melbourne',
        state: 'FL',
        stateName: 'Florida',
        region: 'Mid-Atlantic & Southeast',
        lat: 28.265,
        lng: -80.715,
        venue: 'Space Coast Daily Park (Viera)',
        address: '5775 Stadium Pkwy, Melbourne, FL 32955',
        url: 'https://spacecoastrenaissancefair.com',
        datesText: 'Jan 10–11, 17–18',
        start: '2026-01-10',
        end: '2026-01-18',
        seasons: ['winter'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Melbourne (Greyhound)',
        nearestCityShort: 'Melbourne',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-fl/melbourne/',
    },
    {
        id: 'pinellas-medieval-fair-fl',
        name: 'Pinellas Medieval Fair',
        city: 'Largo',
        state: 'FL',
        stateName: 'Florida',
        region: 'Mid-Atlantic & Southeast',
        lat: 27.908,
        lng: -82.766,
        venue: 'DK Farms and Nursery',
        address: '1750 Lake Ave SE, Largo, FL 33771',
        url: 'https://www.dkfarmsandgardens.com',
        datesText: 'Jan 25–26, Feb 1–2',
        start: '2026-01-25',
        end: '2026-02-02',
        seasons: ['winter'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'St. Petersburg/Tampa',
        nearestCityShort: 'St. Petersburg',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-fl/st-petersburg/',
    },
    {
        id: 'camelot-days-medieval-festival-fl',
        name: 'Camelot Days Medieval Festival',
        city: 'Hollywood',
        state: 'FL',
        stateName: 'Florida',
        region: 'Mid-Atlantic & Southeast',
        lat: 26.033,
        lng: -80.178,
        venue: 'T.Y. Park',
        address: '3300 N Park Rd, Hollywood, FL 33021',
        url: 'https://camelotdays.com',
        datesText: 'November (TBD)',
        start: '2026-11',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Hollywood (Tri-Rail/Brightline)',
        nearestCityShort: 'Hollywood',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-fl/hollywood/',
    },
    {
        id: 'riverdale-kiwanis-medieval-faire-fl',
        name: 'Riverdale Kiwanis Medieval Faire',
        city: 'Fort Myers',
        state: 'FL',
        stateName: 'Florida',
        region: 'Mid-Atlantic & Southeast',
        lat: 26.535,
        lng: -81.888,
        venue: 'Lakes Regional Park',
        address: '7330 Gladiolus Dr, Fort Myers, FL 33908',
        url: 'https://medieval-faire.com',
        datesText: 'Jan/Feb (TBD)',
        start: '2027-01',
        end: null,
        seasons: ['winter'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Fort Myers (Greyhound)',
        nearestCityShort: 'Fort Myers',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-fl/fort-myers/',
    },
    {
        id: 'venezia-renaissance-faire-fl',
        name: 'Venezia Renaissance Faire',
        city: 'Bonita Springs',
        state: 'FL',
        stateName: 'Florida',
        region: 'Mid-Atlantic & Southeast',
        lat: 26.356,
        lng: -81.796,
        venue: 'Old 41 Rd grounds',
        address: '28001 Old 41 Rd, Bonita Springs, FL 34135',
        url: 'https://veneziarenaissancefaire.com',
        datesText: '~Jan 10–18 (TBD)',
        start: '2027-01',
        end: null,
        seasons: ['winter'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Fort Myers/Naples',
        nearestCityShort: 'Fort Myers',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-fl/fort-myers/',
    },
    {
        id: 'texas-renaissance-festival-tx',
        name: 'Texas Renaissance Festival',
        city: 'Todd Mission',
        state: 'TX',
        stateName: 'Texas',
        region: 'South Central',
        lat: 30.3358,
        lng: -95.8567,
        venue: 'TRF grounds',
        address: '21778 FM-1774, Todd Mission, TX 77363',
        url: 'https://texrenfest.com',
        datesText: 'Oct 10 – Nov 29 (+ Thanksgiving Friday)',
        start: '2026-10-10',
        end: '2026-11-29',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'Houston',
        nearestCityShort: 'Houston',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tx/houston/',
    },
    {
        id: 'scarborough-renaissance-festival-tx',
        name: 'Scarborough Renaissance Festival',
        city: 'Waxahachie',
        state: 'TX',
        stateName: 'Texas',
        region: 'South Central',
        lat: 32.3878,
        lng: -96.8788,
        venue: 'Scarborough grounds',
        address: '2511 FM 66, Waxahachie, TX 75167',
        url: 'https://www.srfestival.com',
        datesText: 'Apr 4 – May 25 (8 weekends)',
        start: '2026-04-04',
        end: '2026-05-25',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: true,
        nearestCity: 'Dallas',
        nearestCityShort: 'Dallas',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tx/dallas/',
    },
    {
        id: 'sherwood-forest-faire-tx',
        name: 'Sherwood Forest Faire',
        city: 'McDade',
        state: 'TX',
        stateName: 'Texas',
        region: 'South Central',
        lat: 30.2676,
        lng: -97.234,
        venue: 'Sherwood Forest medieval village',
        address: '1883 Old Highway 20, McDade, TX 78650',
        url: 'https://sherwoodforestfaire.com',
        datesText: 'Feb 28 – Apr 19',
        start: '2026-02-28',
        end: '2026-04-19',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Austin',
        nearestCityShort: 'Austin',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tx/austin/',
    },
    {
        id: 'kerrville-renaissance-festival-tx',
        name: 'Kerrville Renaissance Festival',
        city: 'Kerrville',
        state: 'TX',
        stateName: 'Texas',
        region: 'South Central',
        lat: 30.054,
        lng: -99.08,
        venue: 'River Star Arts & Event Park',
        address: '3785 TX-27, Kerrville, TX 78028',
        url: null,
        datesText: 'Jan 17 – Feb 1',
        start: '2026-01-17',
        end: '2026-02-01',
        seasons: ['winter'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'San Antonio',
        nearestCityShort: 'San Antonio',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tx/san-antonio/',
    },
    {
        id: 'faire-of-champions-tx',
        name: 'Faire of Champions',
        city: 'Palestine',
        state: 'TX',
        stateName: 'Texas',
        region: 'South Central',
        lat: 31.84,
        lng: -95.63,
        venue: 'Faire of Champions grounds',
        address: '4033 AN County Rd 2212, Palestine, TX 75803',
        url: 'https://faireofchampions.com',
        datesText: 'Jan 3 – Feb 8',
        start: '2026-01-03',
        end: '2026-02-08',
        seasons: ['winter'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Dallas/Tyler',
        nearestCityShort: 'Dallas',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tx/dallas/',
    },
    {
        id: 'whispering-grove-faire-tx',
        name: 'Whispering Grove Faire',
        city: 'Kirbyville',
        state: 'TX',
        stateName: 'Texas',
        region: 'South Central',
        lat: 30.367,
        lng: -94.308,
        venue: 'Whispering Grove (Kountze)',
        address: '2785 FM 770, Kountze, TX 77625',
        url: 'https://whisperinggrovefaire.com',
        datesText: 'Apr 25 – May 17',
        start: '2026-04-25',
        end: '2026-05-17',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Beaumont',
        nearestCityShort: 'Beaumont',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tx/beaumont/',
    },
    {
        id: 'west-texas-renaissance-festival-tx',
        name: 'West Texas Renaissance Festival',
        city: 'Odessa',
        state: 'TX',
        stateName: 'Texas',
        region: 'South Central',
        lat: 31.905,
        lng: -102.34,
        venue: 'VFW Post 4372',
        address: '208 E VFW Ln, Odessa, TX 79762',
        url: 'https://www.westtexasren.com',
        datesText: 'Jun 6–7 (1st annual)',
        start: '2026-06-06',
        end: '2026-06-07',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Midland–Odessa',
        nearestCityShort: 'Midland',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tx/midland/',
    },
    {
        id: 'new-braunfels-renaissance-faire-tx',
        name: 'New Braunfels Renaissance Faire',
        city: 'New Braunfels',
        state: 'TX',
        stateName: 'Texas',
        region: 'South Central',
        lat: 29.708,
        lng: -98.118,
        venue: 'Heritage Village',
        address: '1370 Church Hill Dr, New Braunfels, TX 78130',
        url: 'https://www.nbrenfaire.com',
        datesText: 'Dec 4–6, 11–13',
        start: '2026-12-04',
        end: '2026-12-13',
        seasons: ['winter'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'San Antonio',
        nearestCityShort: 'San Antonio',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tx/san-antonio/',
    },
    {
        id: 'four-winds-renaissance-faire-tx',
        name: 'Four Winds Renaissance Faire',
        city: 'Troup',
        state: 'TX',
        stateName: 'Texas',
        region: 'South Central',
        lat: 32.19,
        lng: -95.19,
        venue: 'Four Winds grounds',
        address: '21852 CR 2178, Troup, TX 75789',
        url: 'https://fourwindsfaire.com',
        datesText: 'Mar–Apr (TBD)',
        start: '2027-03',
        end: null,
        seasons: ['spring'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Tyler',
        nearestCityShort: 'Tyler',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tx/tyler/',
    },
    {
        id: 'middlefaire-tx',
        name: 'Middlefaire',
        city: 'Hillsboro',
        state: 'TX',
        stateName: 'Texas',
        region: 'South Central',
        lat: 32.093,
        lng: -97.147,
        venue: 'Middlefaire grounds',
        address: '8581 State Hwy 171, Hillsboro, TX 76645',
        url: null,
        datesText: 'Sep/Nov (TBD)',
        start: '2026-09',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Waco/Dallas',
        nearestCityShort: 'Waco',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tx/waco/',
    },
    {
        id: 'oklahoma-renaissance-festival-ok',
        name: 'Oklahoma Renaissance Festival',
        city: 'Muskogee',
        state: 'OK',
        stateName: 'Oklahoma',
        region: 'South Central',
        lat: 35.7487,
        lng: -95.3844,
        venue: 'The Castle of Muskogee',
        address: '3400 Fern Mountain Rd, Muskogee, OK 74401',
        url: 'https://okcastle.com',
        datesText: 'Apr 25 – May 31 (Castle of Muskogee)',
        start: '2026-04-25',
        end: '2026-05-31',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Tulsa',
        nearestCityShort: 'Tulsa',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ok/tulsa/',
    },
    {
        id: 'medieval-fair-of-norman-ok',
        name: 'Medieval Fair of Norman',
        city: 'Norman',
        state: 'OK',
        stateName: 'Oklahoma',
        region: 'South Central',
        lat: 35.1934,
        lng: -97.4445,
        venue: 'Reaves Park',
        address: '2501 Jenkins Ave, Norman, OK 73072',
        url: 'https://www.medievalfair.org',
        datesText: '~Apr 10–12',
        start: '2026-04-10',
        end: '2026-04-12',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Oklahoma City (Amtrak in Norman)',
        nearestCityShort: 'Oklahoma City',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ok/oklahoma-city/',
    },
    {
        id: 'louisiana-renaissance-festival-la',
        name: 'Louisiana Renaissance Festival',
        city: 'Hammond',
        state: 'LA',
        stateName: 'Louisiana',
        region: 'South Central',
        lat: 30.456,
        lng: -90.387,
        venue: 'LRF grounds',
        address: '46468 River Rd, Hammond, LA 70401',
        url: 'https://www.renfest.net',
        datesText: 'Nov 7 – Dec 13',
        start: '2026-11-07',
        end: '2026-12-13',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Hammond (Amtrak); New Orleans',
        nearestCityShort: 'New Orleans',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-la/new-orleans/',
    },
    {
        id: 'arkansas-renaissance-festival-ar',
        name: 'Arkansas Renaissance Festival',
        city: 'Mount Vernon',
        state: 'AR',
        stateName: 'Arkansas',
        region: 'South Central',
        lat: 35.223,
        lng: -92.135,
        venue: 'Dragonstone Springs',
        address: '275 Adkisson Road, Mount Vernon, AR 72111',
        url: 'https://arrenfest.com',
        datesText: 'Aug 29 – Oct 4 (6 weekends)',
        start: '2026-08-29',
        end: '2026-10-04',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Little Rock',
        nearestCityShort: 'Little Rock',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ar/little-rock/',
    },
    {
        id: 'festival-of-the-fae-spring-ren-faire-ms',
        name: 'Festival of the Fae (Spring Ren Faire)',
        city: 'Columbus',
        state: 'MS',
        stateName: 'Mississippi',
        region: 'South Central',
        lat: 33.556,
        lng: -88.36,
        venue: 'The Fae Grounds',
        address: '43 Luxilin Dr, Columbus, MS 39705',
        url: 'https://festivalofthefae.com',
        datesText: 'May 16–17',
        start: '2026-05-16',
        end: '2026-05-17',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Tupelo/Columbus',
        nearestCityShort: 'Tupelo',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ms/tupelo/',
    },
    {
        id: 'alabama-renaissance-faire-al',
        name: 'Alabama Renaissance Faire',
        city: 'Florence',
        state: 'AL',
        stateName: 'Alabama',
        region: 'South Central',
        lat: 34.7998,
        lng: -87.6772,
        venue: 'Wilson Park',
        address: '217 E Tuscaloosa St, Florence, AL 35630',
        url: 'https://www.thealrenfaire.org',
        datesText: 'Oct 24–25 (free)',
        start: '2026-10-24',
        end: '2026-10-25',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Huntsville',
        nearestCityShort: 'Huntsville',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-al/huntsville/',
    },
    {
        id: 'scottsboro-renaissance-faire-al',
        name: 'Scottsboro Renaissance Faire',
        city: 'Scottsboro',
        state: 'AL',
        stateName: 'Alabama',
        region: 'South Central',
        lat: 34.672,
        lng: -86.034,
        venue: 'King Caldwell Park',
        address: 'King Caldwell Park, Scottsboro, AL 35768',
        url: null,
        datesText: 'Nov 14–15',
        start: '2026-11-14',
        end: '2026-11-15',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Huntsville/Chattanooga',
        nearestCityShort: 'Huntsville',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-al/huntsville/',
    },
    {
        id: 'tennessee-renaissance-festival-tn',
        name: 'Tennessee Renaissance Festival',
        city: 'Arrington',
        state: 'TN',
        stateName: 'Tennessee',
        region: 'South Central',
        lat: 35.856,
        lng: -86.681,
        venue: 'Castle Gwynn',
        address: '2135 Newcastle Road, Arrington, TN 37014',
        url: 'https://www.tnrenfest.com',
        datesText: 'May 2–31 (40th)',
        start: '2026-05-02',
        end: '2026-05-31',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Nashville',
        nearestCityShort: 'Nashville',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tn/nashville/',
    },
    {
        id: 'chattanooga-renaissance-faire-tn',
        name: 'Chattanooga Renaissance Faire',
        city: 'Chattanooga',
        state: 'TN',
        stateName: 'Tennessee',
        region: 'South Central',
        lat: 35.06127,
        lng: -85.30715,
        venue: 'Coolidge Park',
        address: '150 River St, Chattanooga, TN 37405',
        url: 'https://chattrenfaire.com',
        datesText: 'Nov 14–15',
        start: '2026-11-14',
        end: '2026-11-15',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Chattanooga',
        nearestCityShort: 'Chattanooga',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tn/chattanooga/',
    },
    {
        id: 'east-tennessee-renaissance-faire-tn',
        name: 'East Tennessee Renaissance Faire',
        city: 'Sevierville',
        state: 'TN',
        stateName: 'Tennessee',
        region: 'South Central',
        lat: 35.876,
        lng: -83.598,
        venue: 'Sevier County Fairgrounds',
        address: '754 Old Knoxville Hwy, Sevierville, TN 37862',
        url: null,
        datesText: 'May 23–24',
        start: '2026-05-23',
        end: '2026-05-24',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Knoxville',
        nearestCityShort: 'Knoxville',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tn/knoxville/',
    },
    {
        id: 'tennessee-medieval-faire-tn',
        name: 'Tennessee Medieval Faire',
        city: 'Harriman',
        state: 'TN',
        stateName: 'Tennessee',
        region: 'South Central',
        lat: 35.932,
        lng: -84.523,
        venue: 'TMF grounds',
        address: '550 Fiske Rd, Harriman, TN 37748',
        url: 'https://tmfaire.com',
        datesText: 'Early Oct (TBD; on hold 2025)',
        start: '2026-10',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Knoxville',
        nearestCityShort: 'Knoxville',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tn/knoxville/',
    },
    {
        id: 'appalachian-renaissance-faire-tn',
        name: 'Appalachian Renaissance Faire',
        city: 'Piney Flats',
        state: 'TN',
        stateName: 'Tennessee',
        region: 'South Central',
        lat: 36.419,
        lng: -82.354,
        venue: 'East Tennessee Distillery',
        address: '220 Piney Flats Rd, Piney Flats, TN 37686',
        url: 'https://appalachianrenaissancefaire.com',
        datesText: 'Fall (TBD)',
        start: '2026-10',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Johnson City/Bristol',
        nearestCityShort: 'Johnson City',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tn/johnson-city/',
    },
    {
        id: 'kentucky-highland-renaissance-festival-ky',
        name: 'Kentucky Highland Renaissance Festival',
        city: 'Eminence',
        state: 'KY',
        stateName: 'Kentucky',
        region: 'South Central',
        lat: 38.372,
        lng: -85.178,
        venue: 'KHRF grounds',
        address: '955 Elm St, Eminence, KY 40019',
        url: 'https://kyrenfaire.com',
        datesText: 'May 30 – Jul 19 (8 weekends)',
        start: '2026-05-30',
        end: '2026-07-19',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Louisville',
        nearestCityShort: 'Louisville',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ky/louisville/',
    },
    {
        id: 'vandalia-renaissance-and-fantasy-faire-ky',
        name: 'Vandalia Renaissance & Fantasy Faire',
        city: 'Olive Hill',
        state: 'KY',
        stateName: 'Kentucky',
        region: 'South Central',
        lat: 38.305,
        lng: -83.174,
        venue: 'Carter County Shrine Show Grounds',
        address: 'Olive Hill, KY 41164',
        url: 'https://vandaliarenfaire.com',
        datesText: 'Oct 9–11',
        start: '2026-10-09',
        end: '2026-10-11',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Ashland/Lexington',
        nearestCityShort: 'Lexington',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ky/lexington/',
    },
    {
        id: 'ohio-renaissance-festival-oh',
        name: 'Ohio Renaissance Festival',
        city: 'Harveysburg',
        state: 'OH',
        stateName: 'Ohio',
        region: 'Great Lakes',
        lat: 39.50002,
        lng: -83.9977,
        venue: 'Renaissance Park',
        address: '10542 E State Route 73, Waynesville, OH 45068',
        url: 'https://www.renfestival.com',
        datesText: 'Sep 5 – Nov 1',
        start: '2026-09-05',
        end: '2026-11-01',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'Cincinnati (Amtrak); Dayton',
        nearestCityShort: 'Cincinnati',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-oh/cincinnati/',
    },
    {
        id: 'great-lakes-medieval-faire-oh',
        name: 'Great Lakes Medieval Faire',
        city: 'Rock Creek',
        state: 'OH',
        stateName: 'Ohio',
        region: 'Great Lakes',
        lat: 41.66611,
        lng: -80.95917,
        venue: 'GLMF Grounds',
        address: '3033 State Route 534, Rock Creek, OH 44084',
        url: 'https://www.medievalfaire.com',
        datesText: 'Jul 11 – Aug 23 (7 weekends)',
        start: '2026-07-11',
        end: '2026-08-23',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Cleveland; Ashtabula (Amtrak)',
        nearestCityShort: 'Cleveland',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-oh/cleveland/',
    },
    {
        id: 'great-camelot-renaissance-faire-oh-oh',
        name: 'Great Camelot Renaissance Faire (OH)',
        city: 'Carrollton',
        state: 'OH',
        stateName: 'Ohio',
        region: 'Great Lakes',
        lat: 40.5875,
        lng: -81.09214,
        venue: 'Century Farms',
        address: '1121 Canton Rd NW, Carrollton, OH 44615',
        url: 'https://www.camelotrenfaire.com/events/carrollton-oh-great-camelot-renaissance-faire',
        datesText: 'Aug 15–16',
        start: '2026-08-15',
        end: '2026-08-16',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Canton (Barons Bus)',
        nearestCityShort: 'Canton',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-oh/canton/',
    },
    {
        id: 'hocking-hills-medieval-festival-oh',
        name: 'Hocking Hills Medieval Festival',
        city: 'Logan',
        state: 'OH',
        stateName: 'Ohio',
        region: 'Great Lakes',
        lat: 39.54052,
        lng: -82.39665,
        venue: 'Hocking County Fairgrounds',
        address: '100 N Homer Ave, Logan, OH 43138',
        url: 'https://www.explorehockinghills.com/festivals-events/hocking-hills-medieval-fest/',
        datesText: 'Aug 29–30',
        start: '2026-08-29',
        end: '2026-08-30',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Columbus; Athens (GoBus)',
        nearestCityShort: 'Columbus',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-oh/columbus/',
    },
    {
        id: 'days-of-knights-oh',
        name: 'Days of Knights',
        city: 'Lancaster',
        state: 'OH',
        stateName: 'Ohio',
        region: 'Great Lakes',
        lat: 39.6832,
        lng: -82.57538,
        venue: 'Charles Alley Nature Park',
        address: '2805 Old Logan Rd SE, Lancaster, OH 43130',
        url: 'https://thedaysofknights.com',
        datesText: 'Oct 10–11',
        start: '2026-10-10',
        end: '2026-10-11',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Columbus (GoBus)',
        nearestCityShort: 'Columbus',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-oh/columbus/',
    },
    {
        id: 'rio-renaissance-faire-oh',
        name: 'Rio Renaissance Faire',
        city: 'Bidwell',
        state: 'OH',
        stateName: 'Ohio',
        region: 'Great Lakes',
        lat: 38.91869,
        lng: -82.29904,
        venue: 'Rio Ridge Venue',
        address: '10854 State Route 588, Bidwell, OH 45674',
        url: 'https://www.finalbosscon.com/riorenaissancefaire',
        datesText: 'Sep 12',
        start: '2026-09-12',
        end: '2026-09-12',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Huntington WV (Amtrak)',
        nearestCityShort: 'Huntington',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wv/huntington/',
    },
    {
        id: 'black-swamp-ren-fest-oh',
        name: 'Black Swamp Ren Fest',
        city: 'Toledo',
        state: 'OH',
        stateName: 'Ohio',
        region: 'Great Lakes',
        lat: 41.65517,
        lng: -83.5446,
        venue: 'The Toledo Club',
        address: '235 14th St, Toledo, OH 43604',
        url: 'https://www.gatheringofkingdoms.com',
        datesText: 'Oct 17',
        start: '2026-10-17',
        end: '2026-10-17',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Toledo (Amtrak)',
        nearestCityShort: 'Toledo',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-oh/toledo/',
    },
    {
        id: 'cambridge-medieval-market-faire-oh',
        name: 'Cambridge Medieval Market Faire',
        city: 'Cambridge',
        state: 'OH',
        stateName: 'Ohio',
        region: 'Great Lakes',
        lat: 40.03,
        lng: -81.59,
        venue: null,
        address: null,
        url: null,
        datesText: 'October (TBD)',
        start: '2026-10',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Cambridge (Barons Bus)',
        nearestCityShort: 'Cambridge',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-oh/cambridge/',
    },
    {
        id: 'indiana-renaissance-faire-in',
        name: 'Indiana Renaissance Faire',
        city: 'Nineveh',
        state: 'IN',
        stateName: 'Indiana',
        region: 'Great Lakes',
        lat: 39.36111,
        lng: -86.09278,
        venue: 'Johnson County Park (Amphitheater)',
        address: '3097 E 32nd St, Nineveh, IN 46164',
        url: 'https://indianarenfest.com',
        datesText: 'Jun 13–14, 20–21',
        start: '2026-06-13',
        end: '2026-06-21',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Indianapolis',
        nearestCityShort: 'Indianapolis',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-in/indianapolis/',
    },
    {
        id: 'southern-indiana-renaissance-faire-in',
        name: 'Southern Indiana Renaissance Faire',
        city: 'Seymour',
        state: 'IN',
        stateName: 'Indiana',
        region: 'Great Lakes',
        lat: 38.95878,
        lng: -85.83467,
        venue: 'Swifty Farms',
        address: '351 US-31, Seymour, IN 47274',
        url: 'https://renaissancefaireindiana.com',
        datesText: 'Apr 25 – May 24',
        start: '2026-04-25',
        end: '2026-05-24',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Indianapolis',
        nearestCityShort: 'Indianapolis',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-in/indianapolis/',
    },
    {
        id: 'great-camelot-renaissance-faire-in-in',
        name: 'Great Camelot Renaissance Faire (IN)',
        city: 'Logansport',
        state: 'IN',
        stateName: 'Indiana',
        region: 'Great Lakes',
        lat: 40.78465,
        lng: -86.33288,
        venue: 'Cass County 4-H Fairground',
        address: '2281 E County Road 125 N, Logansport, IN 46947',
        url: 'https://www.camelotrenfaire.com/logansport-in',
        datesText: 'Aug 8–9',
        start: '2026-08-08',
        end: '2026-08-09',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Lafayette (Amtrak/FlixBus)',
        nearestCityShort: 'Lafayette',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-in/lafayette/',
    },
    {
        id: 'clayshire-castle-medieval-faire-in',
        name: 'Clayshire Castle Medieval Faire',
        city: 'Bowling Green',
        state: 'IN',
        stateName: 'Indiana',
        region: 'Great Lakes',
        lat: 39.39229,
        lng: -86.94369,
        venue: 'Clayshire Castle',
        address: '8780 E County Rd 75 N, Bowling Green, IN 47833',
        url: 'https://www.clayshirecastle.com',
        datesText: 'Sep 26–27',
        start: '2026-09-26',
        end: '2026-09-27',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Terre Haute',
        nearestCityShort: 'Terre Haute',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-in/terre-haute/',
    },
    {
        id: 'new-castle-henry-county-renaissance-festival-in',
        name: 'New Castle–Henry County Renaissance Festival',
        city: 'New Castle',
        state: 'IN',
        stateName: 'Indiana',
        region: 'Great Lakes',
        lat: 39.9302,
        lng: -85.36723,
        venue: 'Arts Park / 1400 Plaza',
        address: '218 S 15th St, New Castle, IN 47362',
        url: 'https://www.nchcrenfest.org',
        datesText: 'Nov 7 (free)',
        start: '2026-11-07',
        end: '2026-11-07',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Indianapolis',
        nearestCityShort: 'Indianapolis',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-in/indianapolis/',
    },
    {
        id: 'renes-ren-fest-in',
        name: "Rene's Ren Fest",
        city: 'Manilla',
        state: 'IN',
        stateName: 'Indiana',
        region: 'Great Lakes',
        lat: 39.57421,
        lng: -85.61942,
        venue: 'Private grounds (Manilla)',
        address: '2605 S Cross St, Manilla, IN 46150',
        url: 'https://gurbyrene.com',
        datesText: 'Late Sep (TBD)',
        start: '2026-09',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Indianapolis',
        nearestCityShort: 'Indianapolis',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-in/indianapolis/',
    },
    {
        id: 'enchanted-lakes-renaissance-faire-in',
        name: 'Enchanted Lakes Renaissance Faire',
        city: 'Angola',
        state: 'IN',
        stateName: 'Indiana',
        region: 'Great Lakes',
        lat: 41.66758,
        lng: -85.03548,
        venue: 'Steuben County Park (Crooked Lake)',
        address: '100 Lane 101 Crooked Lake, Angola, IN 46703',
        url: null,
        datesText: 'June (TBD)',
        start: '2026-06',
        end: null,
        seasons: ['summer'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Fort Wayne',
        nearestCityShort: 'Fort Wayne',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-in/fort-wayne/',
    },
    {
        id: 'michigan-renaissance-festival-mi',
        name: 'Michigan Renaissance Festival',
        city: 'Holly',
        state: 'MI',
        stateName: 'Michigan',
        region: 'Great Lakes',
        lat: 42.84303,
        lng: -83.56924,
        venue: 'MI Ren Fest grounds',
        address: '12600 Dixie Hwy, Holly, MI 48442',
        url: 'https://www.michrenfest.com',
        datesText: 'Aug 22 – Oct 4 (+ Labor Day)',
        start: '2026-08-22',
        end: '2026-10-04',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'Flint (Amtrak Blue Water, Indian Trails)',
        nearestCityShort: 'Flint',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mi/flint/',
    },
    {
        id: 'mid-michigan-renaissance-festival-mi',
        name: 'Mid-Michigan Renaissance Festival',
        city: 'Vassar',
        state: 'MI',
        stateName: 'Michigan',
        region: 'Great Lakes',
        lat: 43.36246,
        lng: -83.59706,
        venue: 'The Enchanted Forest',
        address: '7464 Frankenmuth Rd, Vassar, MI 48768',
        url: 'https://midmichiganrenfest.com',
        datesText: 'Jul 11–26',
        start: '2026-07-11',
        end: '2026-07-26',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Flint',
        nearestCityShort: 'Flint',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mi/flint/',
    },
    {
        id: 'michigan-fantasy-festival-mi',
        name: 'Michigan Fantasy Festival',
        city: 'St. Clair Shores',
        state: 'MI',
        stateName: 'Michigan',
        region: 'Great Lakes',
        lat: 42.50053,
        lng: -82.88468,
        venue: 'VFW Post 1146',
        address: '28404 Jefferson Ave, St. Clair Shores, MI 48088',
        url: 'https://michiganfantasyfestival.com',
        datesText: 'Jul 11–12',
        start: '2026-07-11',
        end: '2026-07-12',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Detroit',
        nearestCityShort: 'Detroit',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mi/detroit/',
    },
    {
        id: 'great-raven-renaissance-festival-mi',
        name: 'Great Raven Renaissance Festival',
        city: 'Galesburg',
        state: 'MI',
        stateName: 'Michigan',
        region: 'Great Lakes',
        lat: 42.28853,
        lng: -85.4185,
        venue: 'Riverside Park',
        address: '33 W Battle Creek St, Galesburg, MI 49053',
        url: 'https://www.greatravenren.com',
        datesText: 'Oct 10–11',
        start: '2026-10-10',
        end: '2026-10-11',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Kalamazoo',
        nearestCityShort: 'Kalamazoo',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mi/kalamazoo/',
    },
    {
        id: 'royal-stagg-renaissance-faire-mi',
        name: 'Royal Stagg Renaissance Faire',
        city: 'Marshall',
        state: 'MI',
        stateName: 'Michigan',
        region: 'Great Lakes',
        lat: 42.26585,
        lng: -84.95121,
        venue: 'Calhoun County Fairgrounds',
        address: '720 Fair St, Marshall, MI 49068',
        url: 'https://www.royalstagg.com',
        datesText: 'May 9–10',
        start: '2026-05-09',
        end: '2026-05-10',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Battle Creek',
        nearestCityShort: 'Battle Creek',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mi/battle-creek/',
    },
    {
        id: 'grand-valley-renaissance-festival-mi',
        name: 'Grand Valley Renaissance Festival',
        city: 'Allendale',
        state: 'MI',
        stateName: 'Michigan',
        region: 'Great Lakes',
        lat: 42.96317,
        lng: -85.89058,
        venue: 'GVSU campus',
        address: '1 Campus Dr, Allendale, MI 49401',
        url: 'http://www.gvren.org',
        datesText: 'Early Oct (TBD; free)',
        start: '2026-10',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Grand Rapids',
        nearestCityShort: 'Grand Rapids',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mi/grand-rapids/',
    },
    {
        id: 'niles-renaissance-faire-mi',
        name: 'Niles Renaissance Faire',
        city: 'Niles',
        state: 'MI',
        stateName: 'Michigan',
        region: 'Great Lakes',
        lat: 41.84132,
        lng: -86.25658,
        venue: 'Plym Park',
        address: '401 Marmont St, Niles, MI 49120',
        url: 'https://www.nilesrenaissancefaire.com',
        datesText: '~May 15–17',
        start: '2026-05-15',
        end: '2026-05-17',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Niles (Amtrak); South Bend',
        nearestCityShort: 'South Bend',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-in/south-bend/',
    },
    {
        id: 'greenville-renaissance-faire-mi',
        name: 'Greenville Renaissance Faire',
        city: 'Greenville',
        state: 'MI',
        stateName: 'Michigan',
        region: 'Great Lakes',
        lat: 43.17768,
        lng: -85.23976,
        venue: '900 E Kent Rd',
        address: '900 E Kent Rd, Greenville, MI 48838',
        url: null,
        datesText: 'Sep 19',
        start: '2026-09-19',
        end: '2026-09-19',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Grand Rapids',
        nearestCityShort: 'Grand Rapids',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mi/grand-rapids/',
    },
    {
        id: 'faire-in-the-field-il',
        name: 'Faire in the Field',
        city: 'Chicago',
        state: 'IL',
        stateName: 'Illinois',
        region: 'Great Lakes',
        lat: 41.96373,
        lng: -87.64931,
        venue: 'Clarendon Community Center Park',
        address: '4501 N Clarendon Ave, Chicago, IL 60640',
        url: 'https://www.faireinthefield.com',
        datesText: 'Jun 13 (free)',
        start: '2026-06-13',
        end: '2026-06-13',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Chicago (Union Station)',
        nearestCityShort: 'Chicago',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-il/chicago/',
    },
    {
        id: 'olde-english-faire-at-stronghold-castle-il',
        name: 'Olde English Faire at Stronghold Castle',
        city: 'Oregon',
        state: 'IL',
        stateName: 'Illinois',
        region: 'Great Lakes',
        lat: 42.01475,
        lng: -89.33233,
        venue: 'Stronghold Camp & Retreat',
        address: '1922 N IL Route 2, Oregon, IL 61061',
        url: 'https://strongholdcenter.org/Events/Olde-English-Faire',
        datesText: 'Oct 3–4',
        start: '2026-10-03',
        end: '2026-10-04',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Rockford',
        nearestCityShort: 'Rockford',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-il/rockford/',
    },
    {
        id: 'frostmoon-faire-il',
        name: 'Frostmoon Faire',
        city: 'Crystal Lake',
        state: 'IL',
        stateName: 'Illinois',
        region: 'Great Lakes',
        lat: 42.22974,
        lng: -88.34459,
        venue: 'The Dole',
        address: '401 Country Club Rd, Crystal Lake, IL 60014',
        url: 'https://www.creativeartsinc.org',
        datesText: 'Nov 14 (free)',
        start: '2026-11-14',
        end: '2026-11-14',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Chicago (Metra)',
        nearestCityShort: 'Chicago',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-il/chicago/',
    },
    {
        id: 'marseilles-renaissance-faire-il',
        name: 'Marseilles Renaissance Faire',
        city: 'Marseilles',
        state: 'IL',
        stateName: 'Illinois',
        region: 'Great Lakes',
        lat: 41.32871,
        lng: -88.71736,
        venue: 'Riverfront ball fields',
        address: '655 Commercial St, Marseilles, IL 61341',
        url: 'https://www.cityofmarseilles.com/eventscalendar/marseilles-renaissance-faire/',
        datesText: 'Sep 12',
        start: '2026-09-12',
        end: '2026-09-12',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Ottawa; Mendota (Amtrak)',
        nearestCityShort: 'Ottawa',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-il/ottawa/',
    },
    {
        id: 'bristol-renaissance-faire-wi',
        name: 'Bristol Renaissance Faire',
        city: 'Kenosha',
        state: 'WI',
        stateName: 'Wisconsin',
        region: 'Great Lakes',
        lat: 42.49952,
        lng: -87.95693,
        venue: 'Bristol Ren Faire grounds',
        address: '12550 120th Ave, Kenosha, WI 53142',
        url: 'https://www.renfair.com/bristol',
        datesText: 'Jul 11 – Sep 7 (~9 weekends)',
        start: '2026-07-11',
        end: '2026-09-07',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'Kenosha (Amtrak Hiawatha); Milwaukee/Chicago',
        nearestCityShort: 'Kenosha',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wi/kenosha/',
    },
    {
        id: 'dragon-days-fantasy-festival-wi',
        name: 'Dragon Days Fantasy Festival',
        city: 'Lake Geneva',
        state: 'WI',
        stateName: 'Wisconsin',
        region: 'Great Lakes',
        lat: 42.5918,
        lng: -88.43454,
        venue: 'Library Park (citywide)',
        address: 'W Main St, Lake Geneva, WI 53147',
        url: 'https://www.dragondays.org',
        datesText: 'Oct 3–4',
        start: '2026-10-03',
        end: '2026-10-04',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Milwaukee',
        nearestCityShort: 'Milwaukee',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wi/milwaukee/',
    },
    {
        id: 'old-falls-elizabethan-renaissance-faire-wi',
        name: 'Old Falls Elizabethan Renaissance Faire',
        city: 'Menomonee Falls',
        state: 'WI',
        stateName: 'Wisconsin',
        region: 'Great Lakes',
        lat: 43.19076,
        lng: -88.10521,
        venue: 'Old Falls Village Park',
        address: 'N96W15791 County Line Rd, Menomonee Falls, WI 53051',
        url: 'https://www.oldfallsvillagepark.org/renaissance-faire',
        datesText: 'Sep 12–13',
        start: '2026-09-12',
        end: '2026-09-13',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Milwaukee',
        nearestCityShort: 'Milwaukee',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wi/milwaukee/',
    },
    {
        id: 'central-sands-renaissance-faire-wi',
        name: 'Central Sands Renaissance Faire',
        city: 'Amherst',
        state: 'WI',
        stateName: 'Wisconsin',
        region: 'Great Lakes',
        lat: 44.45068,
        lng: -89.28459,
        venue: 'Central Sands Community HS',
        address: '3995 County Road K, Amherst, WI 54406',
        url: 'https://www.centralsands.org/events-one/central-sands-renaissance-faire',
        datesText: 'Sep 26',
        start: '2026-09-26',
        end: '2026-09-26',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Stevens Point/Wausau',
        nearestCityShort: 'Stevens Point',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wi/stevens-point/',
    },
    {
        id: 'middleton-renaissance-faire-wi',
        name: 'Middleton Renaissance Faire',
        city: 'Middleton',
        state: 'WI',
        stateName: 'Wisconsin',
        region: 'Great Lakes',
        lat: 43.09823,
        lng: -89.48747,
        venue: 'Lakeview Park',
        address: 'Middleton, WI 53562',
        url: 'https://www.midlibrary.org',
        datesText: 'Oct 3',
        start: '2026-10-03',
        end: '2026-10-03',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Madison',
        nearestCityShort: 'Madison',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wi/madison/',
    },
    {
        id: 'a-gathering-of-rogues-and-ruffians-wi',
        name: 'A Gathering of Rogues & Ruffians',
        city: 'Baraboo',
        state: 'WI',
        stateName: 'Wisconsin',
        region: 'Great Lakes',
        lat: 43.46487,
        lng: -89.73264,
        venue: 'Circus World',
        address: '550 Water St, Baraboo, WI 53913',
        url: null,
        datesText: 'Oct 10–11 (Circus World)',
        start: '2026-10-10',
        end: '2026-10-11',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Madison',
        nearestCityShort: 'Madison',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wi/madison/',
    },
    {
        id: 'argren-faire-wi',
        name: 'Argren Faire',
        city: 'Milwaukee',
        state: 'WI',
        stateName: 'Wisconsin',
        region: 'Great Lakes',
        lat: 43.071,
        lng: -87.89997,
        venue: 'Black Husky Brewing',
        address: '909 E Locust St, Milwaukee, WI 53212',
        url: null,
        datesText: 'Sep 12',
        start: '2026-09-12',
        end: '2026-09-12',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Milwaukee',
        nearestCityShort: 'Milwaukee',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wi/milwaukee/',
    },
    {
        id: 'fire-in-the-shire-wi',
        name: 'Fire in the Shire',
        city: 'Alma',
        state: 'WI',
        stateName: 'Wisconsin',
        region: 'Great Lakes',
        lat: 44.32028,
        lng: -91.91491,
        venue: 'Castlerock Museum',
        address: '402 S Second St, Alma, WI 54610',
        url: 'https://www.castlerockmuseum.com',
        datesText: 'Jun 6 (Castle Rock Museum)',
        start: '2026-06-06',
        end: '2026-06-06',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'La Crosse (Amtrak)',
        nearestCityShort: 'La Crosse',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wi/la-crosse/',
    },
    {
        id: 'minnesota-renaissance-festival-mn',
        name: 'Minnesota Renaissance Festival',
        city: 'Shakopee',
        state: 'MN',
        stateName: 'Minnesota',
        region: 'Plains & Upper Midwest',
        lat: 44.77121,
        lng: -93.58538,
        venue: "MNRF grounds (Queen's Gate)",
        address: '12364 Chestnut Blvd, Shakopee, MN 55379',
        url: 'https://renaissancefest.com',
        datesText: 'Aug 22 – Oct 4 (+ Labor Day, Fri Oct 2)',
        start: '2026-08-22',
        end: '2026-10-04',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'Minneapolis (Amtrak, Jefferson Lines)',
        nearestCityShort: 'Minneapolis',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mn/minneapolis/',
    },
    {
        id: 'iowa-renaissance-festival-and-gathering-o-celts-ia',
        name: "Iowa Renaissance Festival & Gathering o' Celts",
        city: 'Amana',
        state: 'IA',
        stateName: 'Iowa',
        region: 'Plains & Upper Midwest',
        lat: 41.79908,
        lng: -91.90053,
        venue: 'Middle Amana Community Park',
        address: '910 U Ave, Middle Amana, IA 52307',
        url: 'https://iowarenfest.com',
        datesText: 'May 23–31 & Oct 10–11, 17–18',
        start: '2026-10-10',
        end: '2026-10-18',
        seasons: ['spring', 'fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Cedar Rapids (Burlington Trailways)',
        nearestCityShort: 'Cedar Rapids',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ia/cedar-rapids/',
    },
    {
        id: 'the-renaissance-faire-at-sleepy-hollow-ia',
        name: 'The Renaissance Faire at Sleepy Hollow',
        city: 'Des Moines',
        state: 'IA',
        stateName: 'Iowa',
        region: 'Plains & Upper Midwest',
        lat: 41.6006,
        lng: -93.5449,
        venue: 'Sleepy Hollow Sports Park',
        address: '4051 Dean Ave, Des Moines, IA 50317',
        url: 'https://sleepyhollowrenfaire.com',
        datesText: 'May 2–17 (spring) & Sep 5–20 (fall)',
        start: '2026-09-05',
        end: '2026-09-20',
        seasons: ['spring', 'fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Des Moines (Burlington Trailways)',
        nearestCityShort: 'Des Moines',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ia/des-moines/',
    },
    {
        id: 'prairie-rose-renaissance-festival-ia',
        name: 'Prairie Rose Renaissance Festival',
        city: 'Ames',
        state: 'IA',
        stateName: 'Iowa',
        region: 'Plains & Upper Midwest',
        lat: 42.016,
        lng: -93.635,
        venue: 'former Haunted Forest site',
        address: '1400 S 4th St, Ames, IA 50010',
        url: 'https://amesrenfaire.com',
        datesText: 'Apr 18–19',
        start: '2026-04-18',
        end: '2026-04-19',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Ames/Des Moines (Jefferson Lines)',
        nearestCityShort: 'Des Moines',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ia/des-moines/',
    },
    {
        id: 'st-louis-renaissance-festival-mo',
        name: 'St. Louis Renaissance Festival',
        city: 'Wentzville',
        state: 'MO',
        stateName: 'Missouri',
        region: 'Plains & Upper Midwest',
        lat: 38.7861,
        lng: -90.907,
        venue: 'Rotary Park',
        address: '2577 W Meyer Rd, Wentzville, MO 63385',
        url: 'https://stlrenfest.com',
        datesText: 'Sep 12 – Oct 25 (+ Mon Oct 12)',
        start: '2026-09-12',
        end: '2026-10-25',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'St. Louis (Amtrak/Greyhound)',
        nearestCityShort: 'St. Louis',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mo/st-louis/',
    },
    {
        id: 'central-missouri-renaissance-festival-mo',
        name: 'Central Missouri Renaissance Festival',
        city: 'Kingdom City',
        state: 'MO',
        stateName: 'Missouri',
        region: 'Plains & Upper Midwest',
        lat: 38.92,
        lng: -91.94,
        venue: 'Fairgrounds S of I-70',
        address: '4274 County Rd 220, Kingdom City, MO 65262',
        url: 'https://yourgloriousadventure.com/adventures/cmrf',
        datesText: 'Apr 18 – May 3 & Oct 24–25',
        start: '2026-10-24',
        end: '2026-10-25',
        seasons: ['spring', 'fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Columbia (Megabus); Jefferson City (Amtrak)',
        nearestCityShort: 'Columbia',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mo/columbia/',
    },
    {
        id: 'the-narnia-faire-mo',
        name: 'The Narnia Faire',
        city: 'Fordland',
        state: 'MO',
        stateName: 'Missouri',
        region: 'Plains & Upper Midwest',
        lat: 37.156,
        lng: -92.933,
        venue: 'Chateau Charmant',
        address: '1700 Martins Branch Rd, Fordland, MO 65652',
        url: 'https://www.charmingcastle.com',
        datesText: 'Apr 16–18',
        start: '2026-04-16',
        end: '2026-04-18',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Springfield (Greyhound)',
        nearestCityShort: 'Springfield',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mo/springfield/',
    },
    {
        id: 'kansas-city-renaissance-festival-ks',
        name: 'Kansas City Renaissance Festival',
        city: 'Bonner Springs',
        state: 'KS',
        stateName: 'Kansas',
        region: 'Plains & Upper Midwest',
        lat: 39.113,
        lng: -94.877,
        venue: 'Sandstone / Ag Center campus',
        address: '628 N 126th St, Bonner Springs, KS 66012',
        url: 'https://kcrenfest.com',
        datesText: 'Sep 5 – Oct 18 (+ Labor Day, Mon Oct 12)',
        start: '2026-09-05',
        end: '2026-10-18',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'Kansas City (Amtrak/Greyhound)',
        nearestCityShort: 'Kansas City',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mo/kansas-city/',
    },
    {
        id: 'great-plains-renaissance-festival-ks',
        name: 'Great Plains Renaissance Festival',
        city: 'Wichita',
        state: 'KS',
        stateName: 'Kansas',
        region: 'Plains & Upper Midwest',
        lat: 37.708,
        lng: -97.423,
        venue: 'Sedgwick County Park',
        address: '6300 W 13th St N, Wichita, KS 67212',
        url: 'https://greatplainsrenfest.com',
        datesText: 'Apr 18–19 & Sep 26–27',
        start: '2026-09-26',
        end: '2026-09-27',
        seasons: ['spring', 'fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Wichita (Greyhound); Newton (Amtrak)',
        nearestCityShort: 'Wichita',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ks/wichita/',
    },
    {
        id: 'great-bend-renaissance-faire-ks',
        name: 'Great Bend Renaissance Faire',
        city: 'Great Bend',
        state: 'KS',
        stateName: 'Kansas',
        region: 'Plains & Upper Midwest',
        lat: 38.36,
        lng: -98.76,
        venue: null,
        address: null,
        url: null,
        datesText: 'October (TBD)',
        start: '2026-10',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Wichita; Hutchinson (Amtrak)',
        nearestCityShort: 'Wichita',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ks/wichita/',
    },
    {
        id: 'renaissance-festival-of-nebraska-ne',
        name: 'Renaissance Festival of Nebraska',
        city: 'Papillion',
        state: 'NE',
        stateName: 'Nebraska',
        region: 'Plains & Upper Midwest',
        lat: 41.092,
        lng: -95.977,
        venue: 'Bellevue Berry Farm',
        address: '11001 S 48th St, Papillion, NE 68133',
        url: 'https://renfestnebraska.com',
        datesText: 'May 2–3, 9–10, 16–17',
        start: '2026-05-02',
        end: '2026-05-17',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Omaha (Amtrak/Greyhound)',
        nearestCityShort: 'Omaha',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ne/omaha/',
    },
    {
        id: 'lincoln-renaissance-festival-ne',
        name: 'Lincoln Renaissance Festival',
        city: 'Walton',
        state: 'NE',
        stateName: 'Nebraska',
        region: 'Plains & Upper Midwest',
        lat: 40.79,
        lng: -96.55,
        venue: 'Cornhusker Council Outdoor Ed Center',
        address: '800 S 120th St, Walton, NE 68461',
        url: null,
        datesText: 'Jul 18–19',
        start: '2026-07-18',
        end: '2026-07-19',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Lincoln (Amtrak/Greyhound)',
        nearestCityShort: 'Lincoln',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ne/lincoln/',
    },
    {
        id: 'nebraska-renaissance-faire-and-medieval-pageant-ne',
        name: 'Nebraska Renaissance Faire & Medieval Pageant',
        city: 'Elkhorn',
        state: 'NE',
        stateName: 'Nebraska',
        region: 'Plains & Upper Midwest',
        lat: 41.29256,
        lng: -96.28056,
        venue: 'Elkhorn Shores',
        address: '23101 W Maple Rd, Elkhorn, NE 68022',
        url: 'https://nebfaire.com',
        datesText: 'Aug 29–30',
        start: '2026-08-29',
        end: '2026-08-30',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Omaha (Amtrak/Greyhound)',
        nearestCityShort: 'Omaha',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ne/omaha/',
    },
    {
        id: 'siouxland-renaissance-festival-sd',
        name: 'Siouxland Renaissance Festival',
        city: 'Sioux Falls',
        state: 'SD',
        stateName: 'South Dakota',
        region: 'Plains & Upper Midwest',
        lat: 43.55306,
        lng: -96.77234,
        venue: 'W.H. Lyon Fairgrounds',
        address: '4000 W 12th St, Sioux Falls, SD 57107',
        url: 'https://siouxlandrenfest.com',
        datesText: 'Jun 6–7 (25th)',
        start: '2026-06-06',
        end: '2026-06-07',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Sioux Falls (Jefferson Lines)',
        nearestCityShort: 'Sioux Falls',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-sd/sioux-falls/',
    },
    {
        id: 'red-river-valley-renaissance-faire-nd',
        name: 'Red River Valley Renaissance Faire',
        city: 'West Fargo',
        state: 'ND',
        stateName: 'North Dakota',
        region: 'Plains & Upper Midwest',
        lat: 46.87748,
        lng: -96.92052,
        venue: 'Red River Valley Fairgrounds',
        address: '1805 Main Ave W, West Fargo, ND 58078',
        url: 'https://www.redrivervalleyfair.com/events/rrvrenfaire',
        datesText: 'Late summer/fall (not posted)',
        start: '2026-09',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Fargo (Amtrak; Jefferson Lines)',
        nearestCityShort: 'Fargo',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nd/fargo/',
    },
    {
        id: 'colorado-renaissance-festival-co',
        name: 'Colorado Renaissance Festival',
        city: 'Larkspur',
        state: 'CO',
        stateName: 'Colorado',
        region: 'Mountain West',
        lat: 39.2447,
        lng: -104.8927,
        venue: 'CO Ren Fest grounds',
        address: '650 Perry Park Ave, Larkspur, CO 80118',
        url: 'https://coloradorenaissance.com',
        datesText: 'Jun 13 – Aug 2',
        start: '2026-06-13',
        end: '2026-08-02',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: true,
        nearestCity: 'Colorado Springs/Denver (Bustang)',
        nearestCityShort: 'Colorado Springs',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-co/colorado-springs/',
    },
    {
        id: 'colorado-medieval-festival-co',
        name: 'Colorado Medieval Festival',
        city: 'Loveland',
        state: 'CO',
        stateName: 'Colorado',
        region: 'Mountain West',
        lat: 40.4213,
        lng: -105.1436,
        venue: 'The Savage Woods',
        address: '1750 Savage Rd, Loveland, CO 80538',
        url: 'https://coloradocastle.com',
        datesText: 'Jun 5–7',
        start: '2026-06-05',
        end: '2026-06-07',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Denver (Bustang North)',
        nearestCityShort: 'Denver',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-co/denver/',
    },
    {
        id: 'southern-colorado-renaissance-faire-co',
        name: 'Southern Colorado Renaissance Faire',
        city: 'Florence',
        state: 'CO',
        stateName: 'Colorado',
        region: 'Mountain West',
        lat: 38.4306,
        lng: -105.0986,
        venue: 'Pathfinder Park',
        address: '6655 CO-115, Florence, CO 81226',
        url: null,
        datesText: 'Aug 22–23',
        start: '2026-08-22',
        end: '2026-08-23',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Pueblo (Bustang Outrider); Colorado Springs',
        nearestCityShort: 'Pueblo',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-co/pueblo/',
    },
    {
        id: 'idaho-renaissance-faire-id',
        name: 'Idaho Renaissance Faire',
        city: 'Meridian',
        state: 'ID',
        stateName: 'Idaho',
        region: 'Mountain West',
        lat: 43.61,
        lng: -116.39,
        venue: null,
        address: null,
        url: 'https://idahorenfair.org',
        datesText: 'Oct 10–11',
        start: '2026-10-10',
        end: '2026-10-11',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Boise (Salt Lake Express, Northwestern Trailways)',
        nearestCityShort: 'Boise',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-id/boise/',
    },
    {
        id: 'southern-idaho-renaissance-faire-id',
        name: 'Southern Idaho Renaissance Faire',
        city: 'Burley',
        state: 'ID',
        stateName: 'Idaho',
        region: 'Mountain West',
        lat: 42.53942,
        lng: -113.78669,
        venue: 'Cassia County Fairgrounds',
        address: '1101 Elba Ave, Burley, ID 83318',
        url: 'https://southernidahorenaissancefaire.com',
        datesText: 'Sep 4–5',
        start: '2026-09-04',
        end: '2026-09-05',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Twin Falls; Burley (Salt Lake Express stop)',
        nearestCityShort: 'Twin Falls',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-id/twin-falls/',
    },
    {
        id: 'central-idaho-renaissance-fair-id',
        name: 'Central Idaho Renaissance Fair',
        city: 'Carey',
        state: 'ID',
        stateName: 'Idaho',
        region: 'Mountain West',
        lat: 43.3095,
        lng: -113.944,
        venue: 'Carey grounds',
        address: '20507 N Main St, Carey, ID 83320',
        url: 'https://centralidahorenais.wixsite.com/website',
        datesText: 'Sep 11–12',
        start: '2026-09-11',
        end: '2026-09-12',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Twin Falls/Boise',
        nearestCityShort: 'Twin Falls',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-id/twin-falls/',
    },
    {
        id: 'thornshire-renaissance-faire-id',
        name: 'Thornshire Renaissance Faire',
        city: 'Rigby',
        state: 'ID',
        stateName: 'Idaho',
        region: 'Mountain West',
        lat: 43.69,
        lng: -111.86,
        venue: 'Private field (Rigby)',
        address: '688 N 4200 E, Rigby, ID 83442',
        url: 'https://www.idahorenaissancefaire.com',
        datesText: 'Late Aug (TBD)',
        start: '2026-08',
        end: null,
        seasons: ['summer'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Idaho Falls (Salt Lake Express, Jefferson Lines)',
        nearestCityShort: 'Idaho Falls',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-id/idaho-falls/',
    },
    {
        id: 'age-of-chivalry-renaissance-festival-nv',
        name: 'Age of Chivalry Renaissance Festival',
        city: 'Las Vegas',
        state: 'NV',
        stateName: 'Nevada',
        region: 'Mountain West',
        lat: 36.0637,
        lng: -115.1108,
        venue: 'Sunset Park',
        address: '2601 E Sunset Rd, Las Vegas, NV 89120',
        url: 'https://lvrenfair.com',
        datesText: 'Oct 9–11',
        start: '2026-10-09',
        end: '2026-10-11',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Las Vegas (Greyhound/FlixBus)',
        nearestCityShort: 'Las Vegas',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nv/las-vegas/',
    },
    {
        id: 'robin-hood-renaissance-faire-nv',
        name: 'Robin Hood Renaissance Faire',
        city: 'Lamoille',
        state: 'NV',
        stateName: 'Nevada',
        region: 'Mountain West',
        lat: 40.72389,
        lng: -115.47889,
        venue: 'Lamoille Grove',
        address: '950 Lamoille Hwy, Lamoille, NV 89828',
        url: 'https://nvrenfaire.com',
        datesText: 'Aug 29–30',
        start: '2026-08-29',
        end: '2026-08-30',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Elko (Amtrak California Zephyr)',
        nearestCityShort: 'Elko',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nv/elko/',
    },
    {
        id: 'boulder-city-renaissance-faire-nv',
        name: 'Boulder City Renaissance Faire',
        city: 'Boulder City',
        state: 'NV',
        stateName: 'Nevada',
        region: 'Mountain West',
        lat: 35.98,
        lng: -114.83,
        venue: null,
        address: null,
        url: null,
        datesText: 'Spring (TBD)',
        start: '2027-04',
        end: null,
        seasons: ['spring'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Las Vegas',
        nearestCityShort: 'Las Vegas',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nv/las-vegas/',
    },
    {
        id: 'ely-renaissance-faire-and-pumpkin-festival-nv',
        name: 'Ely Renaissance Faire & Pumpkin Festival',
        city: 'Ely',
        state: 'NV',
        stateName: 'Nevada',
        region: 'Mountain West',
        lat: 39.2477,
        lng: -114.8877,
        venue: 'Broadbent Park',
        address: 'Ely, NV 89301',
        url: 'https://elylions.com/renfaire',
        datesText: 'Oct 17 (TBD)',
        start: '2026-10',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Elko/Las Vegas (remote)',
        nearestCityShort: 'Las Vegas',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nv/las-vegas/',
    },
    {
        id: 'new-mexico-renaissance-celtic-festival-nm',
        name: 'New Mexico Renaissance Celtic Festival',
        city: 'Edgewood',
        state: 'NM',
        stateName: 'New Mexico',
        region: 'Mountain West',
        lat: 35.0619,
        lng: -106.1917,
        venue: 'Wildlife West Nature Park',
        address: '87 N Frontage Rd, Edgewood, NM 87015',
        url: 'https://nmrenfest.net',
        datesText: 'Mar 28–29, Apr 4–5',
        start: '2026-03-28',
        end: '2026-04-05',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Albuquerque (Greyhound/FlixBus, Rail Runner)',
        nearestCityShort: 'Albuquerque',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nm/albuquerque/',
    },
    {
        id: 'santa-fe-renaissance-faire-nm',
        name: 'Santa Fe Renaissance Faire',
        city: 'Santa Fe',
        state: 'NM',
        stateName: 'New Mexico',
        region: 'Mountain West',
        lat: 35.5556,
        lng: -106.0739,
        venue: 'El Rancho de las Golondrinas',
        address: '334 Los Pinos Rd, Santa Fe, NM 87507',
        url: 'https://golondrinas.org',
        datesText: 'Sep 12–13 (El Rancho de las Golondrinas)',
        start: '2026-09-12',
        end: '2026-09-13',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Santa Fe (Rail Runner; Amtrak at Lamy)',
        nearestCityShort: 'Santa Fe',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nm/santa-fe/',
    },
    {
        id: 'lincoln-forest-renaissance-faire-nm',
        name: 'Lincoln Forest Renaissance Faire',
        city: 'Ruidoso',
        state: 'NM',
        stateName: 'New Mexico',
        region: 'Mountain West',
        lat: 33.3318,
        lng: -105.6707,
        venue: 'Wingfield Park',
        address: '300 Center St, Ruidoso, NM 88345',
        url: 'https://www.lincolnforestrenfair.com',
        datesText: 'May 15–17',
        start: '2026-05-15',
        end: '2026-05-17',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'El Paso TX (Amtrak/Greyhound)',
        nearestCityShort: 'El Paso',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-tx/el-paso/',
    },
    {
        id: 'cavern-city-renaissance-festival-nm',
        name: 'Cavern City Renaissance Festival',
        city: 'Carlsbad',
        state: 'NM',
        stateName: 'New Mexico',
        region: 'Mountain West',
        lat: 32.4237,
        lng: -104.2245,
        venue: 'Carlsbad Municipal Beach Park',
        address: 'E Riverside Dr, Carlsbad, NM 88220',
        url: 'https://caverncityrenfest.com',
        datesText: 'Sep 25–26',
        start: '2026-09-25',
        end: '2026-09-26',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Carlsbad (Greyhound/FlixBus)',
        nearestCityShort: 'Carlsbad',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nm/carlsbad/',
    },
    {
        id: 'renaissance-faire-at-grants-rodeo-grounds-nm',
        name: 'Renaissance Faire at Grants Rodeo Grounds',
        city: 'Grants',
        state: 'NM',
        stateName: 'New Mexico',
        region: 'Mountain West',
        lat: 35.1595,
        lng: -107.8536,
        venue: 'Grants Rodeo Grounds & Cow Palace',
        address: '223 Betty Dr, Grants, NM 87020',
        url: null,
        datesText: 'Sep 26–27',
        start: '2026-09-26',
        end: '2026-09-27',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Albuquerque',
        nearestCityShort: 'Albuquerque',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nm/albuquerque/',
    },
    {
        id: 'farmington-renaissance-faire-nm',
        name: 'Farmington Renaissance Faire',
        city: 'Farmington',
        state: 'NM',
        stateName: 'New Mexico',
        region: 'Mountain West',
        lat: 36.73,
        lng: -108.22,
        venue: 'Animas Park (historical)',
        address: null,
        url: 'https://farmingtonnm.org',
        datesText: 'Fall (TBD; free)',
        start: '2026-10',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Farmington; Albuquerque',
        nearestCityShort: 'Albuquerque',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nm/albuquerque/',
    },
    {
        id: 'renaissance-artsfaire-nm',
        name: 'Renaissance ArtsFaire',
        city: 'Las Cruces',
        state: 'NM',
        stateName: 'New Mexico',
        region: 'Mountain West',
        lat: 32.2915,
        lng: -106.7752,
        venue: 'Young Park',
        address: '850 S Walnut Ave, Las Cruces, NM 88001',
        url: 'https://www.daarts.org',
        datesText: 'Early–mid Nov (TBD; 55th)',
        start: '2026-11',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Las Cruces (Amtrak/Greyhound); El Paso',
        nearestCityShort: 'Las Cruces',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-nm/las-cruces/',
    },
    {
        id: 'arizona-renaissance-festival-az',
        name: 'Arizona Renaissance Festival',
        city: 'Gold Canyon',
        state: 'AZ',
        stateName: 'Arizona',
        region: 'Mountain West',
        lat: 33.323,
        lng: -111.3849,
        venue: 'AZ Ren Fest grounds',
        address: '12601 E US Highway 60, Gold Canyon, AZ 85118',
        url: 'https://arizona.renfestinfo.com',
        datesText: 'Feb 6 – Apr 4, 2027',
        start: '2027-02-06',
        end: '2027-04-04',
        seasons: ['spring', 'winter'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'Phoenix (~40 mi)',
        nearestCityShort: 'Phoenix',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-az/phoenix/',
    },
    {
        id: 'two-rivers-renaissance-faire-az',
        name: 'Two Rivers Renaissance Faire',
        city: 'Yuma',
        state: 'AZ',
        stateName: 'Arizona',
        region: 'Mountain West',
        lat: 32.6647,
        lng: -114.5963,
        venue: 'Yuma County Fairgrounds',
        address: '2520 E 32nd St, Yuma, AZ 85365',
        url: 'https://tworiversfaire.com',
        datesText: 'Feb 5–7, 2027',
        start: '2027-02-05',
        end: '2027-02-07',
        seasons: ['winter'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Yuma (Amtrak Sunset Ltd, FlixBus)',
        nearestCityShort: 'Yuma',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-az/yuma/',
    },
    {
        id: 'london-bridge-renaissance-faire-az',
        name: 'London Bridge Renaissance Faire',
        city: 'Lake Havasu City',
        state: 'AZ',
        stateName: 'Arizona',
        region: 'Mountain West',
        lat: 34.4438,
        lng: -114.3357,
        venue: 'SARA Park Rodeo Grounds',
        address: '1740 McCulloch Blvd S, Lake Havasu City, AZ 86406',
        url: 'https://www.londonbridgerenfaire.com',
        datesText: 'Apr 2–4, 2027',
        start: '2027-04-02',
        end: '2027-04-04',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Kingman (~60 mi; Amtrak SW Chief)',
        nearestCityShort: 'Kingman',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-az/kingman/',
    },
    {
        id: 'medieval-mayhem-renaissance-faire-az',
        name: 'Medieval Mayhem Renaissance Faire',
        city: 'Show Low',
        state: 'AZ',
        stateName: 'Arizona',
        region: 'Mountain West',
        lat: 34.2565,
        lng: -110.0245,
        venue: 'Frontier Field Park',
        address: '650 N 9th Pl, Show Low, AZ 85901',
        url: 'https://azmayhem.com',
        datesText: 'Jul 9–11, 2027',
        start: '2027-07-09',
        end: '2027-07-11',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Phoenix (~180 mi; poorly served)',
        nearestCityShort: 'Phoenix',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-az/phoenix/',
    },
    {
        id: 'utah-renaissance-festival-and-fantasy-faire-ut',
        name: 'Utah Renaissance Festival & Fantasy Faire',
        city: 'Marriott-Slaterville',
        state: 'UT',
        stateName: 'Utah',
        region: 'Mountain West',
        lat: 41.246,
        lng: -112.0175,
        venue: 'Utah Ren Fest grounds',
        address: '970 W 17th St, Marriott-Slaterville, UT 84404',
        url: 'https://www.utahrenfest.com',
        datesText: '~May 2027 (TBD)',
        start: '2027-05',
        end: null,
        seasons: ['spring'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Ogden (FrontRunner, FlixBus); Salt Lake City (Amtrak)',
        nearestCityShort: 'Ogden',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ut/ogden/',
    },
    {
        id: 'utah-renaissance-faire-ut',
        name: 'Utah Renaissance Faire',
        city: 'Mona',
        state: 'UT',
        stateName: 'Utah',
        region: 'Mountain West',
        lat: 39.7947,
        lng: -111.857,
        venue: 'Mt. Nebo Botanical Farm',
        address: '3700 Old Hwy 91, Mona, UT 84645',
        url: 'https://utahrenfaire.org',
        datesText: 'Oct 16–18',
        start: '2026-10-16',
        end: '2026-10-18',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Provo (~40 mi; Amtrak, FrontRunner)',
        nearestCityShort: 'Provo',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ut/provo/',
    },
    {
        id: 'tooele-arts-guild-renaissance-faire-ut',
        name: 'Tooele Arts Guild Renaissance Faire',
        city: 'Tooele',
        state: 'UT',
        stateName: 'Utah',
        region: 'Mountain West',
        lat: 40.6389,
        lng: -112.2963,
        venue: 'Historic Benson Grist Mill',
        address: '325 SR-138, Stansbury Park, UT 84074',
        url: 'https://tooelerenfaire.com',
        datesText: 'Sep 18–19',
        start: '2026-09-18',
        end: '2026-09-19',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Salt Lake City (~35 mi)',
        nearestCityShort: 'Salt Lake City',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ut/salt-lake-city/',
    },
    {
        id: 'red-lodge-renaissance-faire-mt',
        name: 'Red Lodge Renaissance Faire',
        city: 'Red Lodge',
        state: 'MT',
        stateName: 'Montana',
        region: 'Mountain West',
        lat: 45.1945,
        lng: -109.253,
        venue: 'Red Lodge Rodeo Grounds',
        address: '101 Rodeo Rd, Red Lodge, MT 59068',
        url: 'https://redlodgerenfaire.com',
        datesText: '~Aug 2027 (TBD)',
        start: '2027-08',
        end: null,
        seasons: ['summer'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Billings (~60 mi; Jefferson Lines)',
        nearestCityShort: 'Billings',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mt/billings/',
    },
    {
        id: 'bozeman-renaissance-festival-mt',
        name: 'Bozeman Renaissance Festival',
        city: 'Bozeman',
        state: 'MT',
        stateName: 'Montana',
        region: 'Mountain West',
        lat: 45.6877,
        lng: -111.035,
        venue: 'Gallatin County Fairgrounds',
        address: '901 N Black Ave, Bozeman, MT 59715',
        url: 'https://bozemanrenfest.com',
        datesText: 'Aug 22–23',
        start: '2026-08-22',
        end: '2026-08-23',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Bozeman (FlixBus, Jefferson Lines, Salt Lake Express)',
        nearestCityShort: 'Bozeman',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mt/bozeman/',
    },
    {
        id: 'missoula-renaissance-festival-mt',
        name: 'Missoula Renaissance Festival',
        city: 'Missoula',
        state: 'MT',
        stateName: 'Montana',
        region: 'Mountain West',
        lat: 46.8555,
        lng: -114.0128,
        venue: 'Missoula County Fairgrounds',
        address: '1101 South Ave W, Missoula, MT 59801',
        url: 'https://missoularenfest.com',
        datesText: 'Sep 19–20',
        start: '2026-09-19',
        end: '2026-09-20',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Missoula (intercity bus hub)',
        nearestCityShort: 'Missoula',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mt/missoula/',
    },
    {
        id: 'all-hallows-medieval-fantasy-faire-mt',
        name: 'All Hallows Medieval Fantasy Faire',
        city: 'Billings',
        state: 'MT',
        stateName: 'Montana',
        region: 'Mountain West',
        lat: 45.7889,
        lng: -108.503,
        venue: 'North Park',
        address: '308 6th Ave N, Billings, MT 59101',
        url: 'https://allhallowsbillings.com',
        datesText: 'Oct 3–4',
        start: '2026-10-03',
        end: '2026-10-04',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Billings (Jefferson Lines)',
        nearestCityShort: 'Billings',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-mt/billings/',
    },
    {
        id: 'winter-knights-of-cheyenne-wy',
        name: 'Winter Knights of Cheyenne',
        city: 'Cheyenne',
        state: 'WY',
        stateName: 'Wyoming',
        region: 'Mountain West',
        lat: 41.1462,
        lng: -104.718,
        venue: 'Event Center at Archer',
        address: '3801 Archer Pkwy, Cheyenne, WY 82009',
        url: 'https://www.cheyenne.org/winterknights',
        datesText: 'Feb 13–14, 2027',
        start: '2027-02-13',
        end: '2027-02-14',
        seasons: ['winter'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Cheyenne (Express Arrow/Greyhound)',
        nearestCityShort: 'Cheyenne',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wy/cheyenne/',
    },
    {
        id: 'high-plains-renaissance-faire-wy',
        name: 'High Plains Renaissance Faire',
        city: 'Casper',
        state: 'WY',
        stateName: 'Wyoming',
        region: 'Mountain West',
        lat: 42.85,
        lng: -106.33,
        venue: null,
        address: null,
        url: 'https://highplainsrenfaire.com',
        datesText: 'Summer 2027 (TBD)',
        start: '2027-07',
        end: null,
        seasons: ['summer'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Casper (Express Arrow)',
        nearestCityShort: 'Casper',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wy/casper/',
    },
    {
        id: 'middle-platte-renaissance-faire-wy',
        name: 'Middle Platte Renaissance Faire',
        city: 'Casper',
        state: 'WY',
        stateName: 'Wyoming',
        region: 'Mountain West',
        lat: 42.8339,
        lng: -106.296,
        venue: 'Central Wyoming Fairgrounds',
        address: '1700 Fairgrounds Rd, Casper, WY 82604',
        url: null,
        datesText: '~Summer 2027 (TBD)',
        start: '2027-07',
        end: null,
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Casper (Express Arrow)',
        nearestCityShort: 'Casper',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wy/casper/',
    },
    {
        id: 'tournament-of-knights-wy',
        name: 'Tournament of Knights',
        city: 'Sheridan',
        state: 'WY',
        stateName: 'Wyoming',
        region: 'Mountain West',
        lat: 44.7867,
        lng: -106.972,
        venue: 'Sheridan County Fairgrounds',
        address: '1753 Victoria St, Sheridan, WY 82801',
        url: 'https://www.sheridanwyoming.org/festivals-events/tournament-of-knights/',
        datesText: '~Jul 2027 (one day)',
        start: '2027-07',
        end: null,
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Sheridan (local bus; Billings ~130 mi)',
        nearestCityShort: 'Sheridan',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wy/sheridan/',
    },
    {
        id: 'original-renaissance-pleasure-faire-ca',
        name: 'Original Renaissance Pleasure Faire',
        city: 'Irwindale',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 34.1171,
        lng: -117.9552,
        venue: 'Santa Fe Dam Recreation Area',
        address: '15501 E Arrow Hwy, Irwindale, CA 91706',
        url: 'https://renfair.com/socal/',
        datesText: 'Apr 4 – May 17',
        start: '2026-04-04',
        end: '2026-05-17',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'Los Angeles (Union Station, Metro)',
        nearestCityShort: 'Los Angeles',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/los-angeles/',
    },
    {
        id: 'northern-california-renaissance-faire-ca',
        name: 'Northern California Renaissance Faire',
        city: 'Hollister',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 36.99335,
        lng: -121.37885,
        venue: 'Casa de Fruta',
        address: '10031 Pacheco Pass Hwy, Hollister, CA 95023',
        url: 'https://norcalrenfaire.com/',
        datesText: 'Sep 19 – Oct 25',
        start: '2026-09-19',
        end: '2026-10-25',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: true,
        nearestCity: 'San Jose/Gilroy (Caltrain, Amtrak)',
        nearestCityShort: 'Gilroy',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/gilroy/',
    },
    {
        id: 'central-coast-renaissance-festival-ca',
        name: 'Central Coast Renaissance Festival',
        city: 'San Luis Obispo',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 35.26692,
        lng: -120.68628,
        venue: 'Laguna Lake Park',
        address: '504 Madonna Rd, San Luis Obispo, CA 93405',
        url: 'https://ccrenfaire.com/',
        datesText: 'Jul 18–19 (40th)',
        start: '2026-07-18',
        end: '2026-07-19',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'San Luis Obispo (Amtrak, FlixBus)',
        nearestCityShort: 'San Luis Obispo',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/san-luis-obispo/',
    },
    {
        id: 'idyllwild-renaissance-faire-ca',
        name: 'Idyllwild Renaissance Faire',
        city: 'Mountain Center',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 33.67947,
        lng: -116.68224,
        venue: 'Hurkey Creek Park',
        address: '56375 CA-74, Mountain Center, CA 92561',
        url: 'https://idyllwildrenfaire.com/',
        datesText: 'Sep 12–13',
        start: '2026-09-12',
        end: '2026-09-13',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Riverside/Palm Springs (FlixBus)',
        nearestCityShort: 'Riverside',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/riverside/',
    },
    {
        id: 'tulare-county-renaissance-festival-ca',
        name: 'Tulare County Renaissance Festival',
        city: 'Visalia',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 36.35,
        lng: -119.22833,
        venue: 'Cutler Park',
        address: 'Houston Ave, Visalia, CA 93292',
        url: 'https://tularecountyrenfair.com/',
        datesText: 'Apr 18–19, 25–26',
        start: '2026-04-18',
        end: '2026-04-26',
        seasons: ['spring'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Visalia/Fresno (Amtrak)',
        nearestCityShort: 'Fresno',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/fresno/',
    },
    {
        id: 'valhalla-renaissance-faire-ca',
        name: 'Valhalla Renaissance Faire',
        city: 'South Lake Tahoe',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 38.85588,
        lng: -120.02627,
        venue: 'Tahoe Paradise Park',
        address: '1011 E San Bernardino Ave, South Lake Tahoe, CA 96150',
        url: 'https://valhallafaire.com/',
        datesText: 'Aug 15–16, 22–23',
        start: '2026-08-15',
        end: '2026-08-23',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'South Lake Tahoe (Amtrak Thruway); Sacramento',
        nearestCityShort: 'South Lake Tahoe',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/south-lake-tahoe/',
    },
    {
        id: 'koroneburg-old-world-renaissance-festival-ca',
        name: 'Koroneburg Old World Renaissance Festival',
        city: 'Corona',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 33.93027,
        lng: -117.60337,
        venue: 'Riverview Recreation Park',
        address: '14600 Baron Dr, Eastvale, CA 92880',
        url: 'https://renfestcorona.com/',
        datesText: '~Late May – June (TBD)',
        start: '2027-05',
        end: null,
        seasons: ['spring', 'summer'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Riverside/LA (Metrolink)',
        nearestCityShort: 'Riverside',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/riverside/',
    },
    {
        id: 'escondido-renaissance-faire-spring-ca',
        name: 'Escondido Renaissance Faire (Spring)',
        city: 'Escondido',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 33.08362,
        lng: -117.08446,
        venue: 'Felicita County Park',
        address: '742 Clarence Ln, Escondido, CA 92029',
        url: 'https://oldetymeproductions.com/',
        datesText: 'Apr 25–26, May 2–3',
        start: '2026-04-25',
        end: '2026-05-03',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Escondido (SPRINTER); San Diego',
        nearestCityShort: 'San Diego',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/san-diego/',
    },
    {
        id: 'escondido-renaissance-faire-fall-ca',
        name: 'Escondido Renaissance Faire (Fall)',
        city: 'Escondido',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 33.08362,
        lng: -117.08446,
        venue: 'Felicita County Park',
        address: '742 Clarence Ln, Escondido, CA 92029',
        url: 'https://oldetymeproductions.com/',
        datesText: 'Late Oct / early Nov (TBD)',
        start: '2026-10',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Escondido/San Diego',
        nearestCityShort: 'San Diego',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/san-diego/',
    },
    {
        id: 'shasta-renaissance-and-fantasy-faire-ca',
        name: 'Shasta Renaissance & Fantasy Faire',
        city: 'Anderson',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 40.45464,
        lng: -122.31076,
        venue: 'Shasta District Fair & Event Center',
        address: '1890 Briggs St, Anderson, CA 96007',
        url: null,
        datesText: 'May 23–24',
        start: '2026-05-23',
        end: '2026-05-24',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Redding (Amtrak, FlixBus)',
        nearestCityShort: 'Redding',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/redding/',
    },
    {
        id: 'yosemite-chivalry-and-fantasy-festival-ca',
        name: 'Yosemite Chivalry & Fantasy Festival',
        city: 'Mariposa',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 37.46386,
        lng: -119.94772,
        venue: 'Mariposa County Fairgrounds',
        address: '5007 Fairgrounds Rd, Mariposa, CA 95338',
        url: 'https://www.chivalryandfantasyfestival.com/',
        datesText: 'Jun 6–7',
        start: '2026-06-06',
        end: '2026-06-07',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Merced (Amtrak + YARTS)',
        nearestCityShort: 'Merced',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/merced/',
    },
    {
        id: 'kearney-park-renaissance-faire-ca',
        name: 'Kearney Park Renaissance Faire',
        city: 'Fresno',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 36.72578,
        lng: -119.91932,
        venue: 'Kearney Park',
        address: '6725 W Kearney Blvd, Fresno, CA 93706',
        url: 'https://www.guildofstmargaretinc.com/',
        datesText: 'Nov 14–15',
        start: '2026-11-14',
        end: '2026-11-15',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Fresno (Amtrak, FlixBus)',
        nearestCityShort: 'Fresno',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/fresno/',
    },
    {
        id: 'el-dorado-fantasy-faire-ca',
        name: 'El Dorado Fantasy Faire',
        city: 'Long Beach',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 33.82305,
        lng: -118.08674,
        venue: 'El Dorado East Regional Park (Area III)',
        address: '7550 E Spring St, Long Beach, CA 90815',
        url: null,
        datesText: 'Jul 31 – Aug 2',
        start: '2026-07-31',
        end: '2026-08-02',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Long Beach/LA (Metro, FlixBus)',
        nearestCityShort: 'Long Beach',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/long-beach/',
    },
    {
        id: 'tem-ren-faire-ca',
        name: 'Tem Ren Faire',
        city: 'Temecula',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 33.48042,
        lng: -117.1008,
        venue: 'Vail Ranch Headquarters',
        address: '32115 Temecula Pkwy, Temecula, CA 92592',
        url: null,
        datesText: 'Nov 15–16',
        start: '2026-11-15',
        end: '2026-11-16',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Temecula/Riverside (FlixBus)',
        nearestCityShort: 'Riverside',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/riverside/',
    },
    {
        id: 'folsom-renaissance-faire-ca',
        name: 'Folsom Renaissance Faire',
        city: 'Folsom',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 38.68288,
        lng: -121.16603,
        venue: 'Folsom City Lions Park',
        address: '403 Stafford St, Folsom, CA 95630',
        url: 'https://www.folsomfaire.com/',
        datesText: '~Sep 27–28 (TBD)',
        start: '2026-09',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Sacramento (Amtrak, SacRT)',
        nearestCityShort: 'Sacramento',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/sacramento/',
    },
    {
        id: 'big-bear-renaissance-faire-ca',
        name: 'Big Bear Renaissance Faire',
        city: 'Big Bear Lake',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 34.24714,
        lng: -116.90134,
        venue: 'Meadow Park',
        address: '41220 Park Ave, Big Bear Lake, CA 92315',
        url: 'https://bigbearrenfaire.org/',
        datesText: 'Summer (TBD)',
        start: '2027-07',
        end: null,
        seasons: ['summer'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'San Bernardino (Metrolink) + shuttle',
        nearestCityShort: 'San Bernardino',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/san-bernardino/',
    },
    {
        id: 'medieval-festival-of-courage-ca',
        name: 'Medieval Festival of Courage',
        city: 'Blue Lake',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 40.88291,
        lng: -123.98395,
        venue: 'Perigot Park',
        address: '210 Chartin Rd, Blue Lake, CA 95525',
        url: 'https://medievalfestivalofcourage.org/',
        datesText: '~Oct 4–5 (TBD)',
        start: '2026-10',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Arcata/Eureka (Amtrak Thruway, Greyhound)',
        nearestCityShort: 'Arcata',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/arcata/',
    },
    {
        id: 'much-ado-about-sebastopol-ca',
        name: 'Much Ado About Sebastopol',
        city: 'Sebastopol',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 38.40019,
        lng: -122.82638,
        venue: 'Ives Park',
        address: '7400 Willow St, Sebastopol, CA 95472',
        url: null,
        datesText: 'September (TBD)',
        start: '2026-09',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Santa Rosa (SMART); San Francisco',
        nearestCityShort: 'Santa Rosa',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/santa-rosa/',
    },
    {
        id: 'all-hallows-fantasy-faire-ca',
        name: 'All Hallows Fantasy Faire',
        city: 'Angels Camp',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 38.04889,
        lng: -120.52139,
        venue: 'Calaveras County Fairgrounds (Frogtown)',
        address: '101 Frogtown Rd, Angels Camp, CA 95222',
        url: 'https://allhallowsfaire.com/',
        datesText: 'October (TBD)',
        start: '2026-10',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Stockton/Sonora (Amtrak Thruway)',
        nearestCityShort: 'Stockton',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/stockton/',
    },
    {
        id: 'nottingham-festival-ca',
        name: 'Nottingham Festival',
        city: 'Simi Valley',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 34.28826,
        lng: -118.72205,
        venue: 'Rancho Simi Community Park',
        address: '3855 Alamo St, Simi Valley, CA 93063',
        url: null,
        datesText: 'November (TBD)',
        start: '2026-11',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Los Angeles/Simi Valley (Metrolink)',
        nearestCityShort: 'Los Angeles',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/los-angeles/',
    },
    {
        id: 'california-city-renaissance-festival-ca',
        name: 'California City Renaissance Festival',
        city: 'California City',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 35.12783,
        lng: -117.94701,
        venue: 'Central Park',
        address: '10460 Heather Ave, California City, CA 93505',
        url: null,
        datesText: 'October (TBD; new)',
        start: '2026-10',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Mojave/Bakersfield (Amtrak)',
        nearestCityShort: 'Bakersfield',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/bakersfield/',
    },
    {
        id: 'great-dickens-christmas-fair-victorian-ca',
        name: 'Great Dickens Christmas Fair (Victorian)',
        city: 'Daly City',
        state: 'CA',
        stateName: 'California',
        region: 'Pacific',
        lat: 37.70618,
        lng: -122.42021,
        venue: 'Cow Palace',
        address: '2600 Geneva Ave, Daly City, CA 94014',
        url: 'https://dickensfair.com/',
        datesText: '~Nov 21 – Dec 20',
        start: '2026-11-21',
        end: '2026-12-20',
        seasons: ['winter'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'San Francisco/Daly City (BART, Caltrain)',
        nearestCityShort: 'San Francisco',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ca/san-francisco/',
    },
    {
        id: 'oregon-renaissance-faire-or',
        name: 'Oregon Renaissance Faire',
        city: 'Canby',
        state: 'OR',
        stateName: 'Oregon',
        region: 'Pacific',
        lat: 45.26704,
        lng: -122.68538,
        venue: 'Clackamas County Fairgrounds',
        address: '694 NE 4th Ave, Canby, OR 97013',
        url: 'https://oregonfaire.com/',
        datesText: 'Jun 6–7, 13–14, 20–21, 27–28',
        start: '2026-06-06',
        end: '2026-06-28',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Portland (Amtrak/FlixBus); Oregon City',
        nearestCityShort: 'Portland',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-or/portland/',
    },
    {
        id: 'canterbury-renaissance-faire-or',
        name: 'Canterbury Renaissance Faire',
        city: 'Silverton',
        state: 'OR',
        stateName: 'Oregon',
        region: 'Pacific',
        lat: 45.00493,
        lng: -122.78329,
        venue: 'Canterbury Faire grounds',
        address: '6569 Valley View Rd, Silverton, OR 97381',
        url: 'https://canterburyfaire.com/',
        datesText: 'Jul 18–19, 25–26',
        start: '2026-07-18',
        end: '2026-07-26',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Salem (Amtrak Cascades, FlixBus)',
        nearestCityShort: 'Salem',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-or/salem/',
    },
    {
        id: 'shrewsbury-renaissance-faire-or',
        name: 'Shrewsbury Renaissance Faire',
        city: 'Kings Valley',
        state: 'OR',
        stateName: 'Oregon',
        region: 'Pacific',
        lat: 44.68442,
        lng: -123.43106,
        venue: 'Shrewsbury grounds',
        address: 'Grant Rd, Kings Valley, OR 97370',
        url: 'https://shrewfaire.org/',
        datesText: 'Sep 12–13',
        start: '2026-09-12',
        end: '2026-09-13',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Corvallis/Albany (Amtrak Cascades)',
        nearestCityShort: 'Corvallis',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-or/corvallis/',
    },
    {
        id: 'wonder-woods-faire-or',
        name: 'Wonder Woods Faire',
        city: 'Medford',
        state: 'OR',
        stateName: 'Oregon',
        region: 'Pacific',
        lat: 42.28894,
        lng: -122.84694,
        venue: 'EdenVale Winery',
        address: '2310 Voorhies Rd, Medford, OR 97501',
        url: 'https://wonderwoodsfaire.com/',
        datesText: 'Sep 19–20 (TBD)',
        start: '2026-09',
        end: null,
        seasons: ['fall'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Medford (FlixBus/Greyhound)',
        nearestCityShort: 'Medford',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-or/medford/',
    },
    {
        id: 'dragons-gate-renaissance-faire-or',
        name: "Dragon's Gate Renaissance Faire",
        city: 'Milton-Freewater',
        state: 'OR',
        stateName: 'Oregon',
        region: 'Pacific',
        lat: 45.93291,
        lng: -118.38776,
        venue: "Dragon's Gate Brewery",
        address: '52288 Sunquist Rd, Milton-Freewater, OR 97862',
        url: 'https://dragonsgatebrewery.com/',
        datesText: 'May 30',
        start: '2026-05-30',
        end: '2026-05-30',
        seasons: ['spring'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Walla Walla WA (bus); Pendleton',
        nearestCityShort: 'Walla Walla',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wa/walla-walla/',
    },
    {
        id: 'ye-merrie-greenwood-at-glastonbury-or',
        name: 'Ye Merrie Greenwood at Glastonbury',
        city: 'Toledo',
        state: 'OR',
        stateName: 'Oregon',
        region: 'Pacific',
        lat: 44.62169,
        lng: -123.9382,
        venue: 'Waterfront Park (Port of Toledo)',
        address: 'NW 1st & A St, Toledo, OR 97391',
        url: null,
        datesText: 'Spring (TBD)',
        start: '2027-04',
        end: null,
        seasons: ['spring'],
        dateConfidence: 'unposted',
        isFlagship: false,
        nearestCity: 'Corvallis/Albany; Newport bus',
        nearestCityShort: 'Corvallis',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-or/corvallis/',
    },
    {
        id: 'washington-midsummer-renaissance-faire-wa',
        name: 'Washington Midsummer Renaissance Faire',
        city: 'Snohomish',
        state: 'WA',
        stateName: 'Washington',
        region: 'Pacific',
        lat: 47.82767,
        lng: -122.00893,
        venue: 'Sky Meadows',
        address: '18601 Sky Meadows Ln, Snohomish, WA 98290',
        url: 'https://washingtonfaire.com/',
        datesText: 'Jul 18 – Aug 16 (5 weekends)',
        start: '2026-07-18',
        end: '2026-08-16',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Everett (Amtrak Cascades, Sound Transit); Seattle',
        nearestCityShort: 'Everett',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wa/everett/',
    },
    {
        id: 'camlann-medieval-village-wa',
        name: 'Camlann Medieval Village',
        city: 'Carnation',
        state: 'WA',
        stateName: 'Washington',
        region: 'Pacific',
        lat: 47.64751,
        lng: -121.91294,
        venue: 'Camlann Medieval Village',
        address: '10320 Kelly Rd NE, Carnation, WA 98014',
        url: 'https://camlann.org/',
        datesText: 'Weekends May–Sep (+ seasonal feasts)',
        start: '2026-05-02',
        end: '2026-09-27',
        seasons: ['spring', 'summer', 'fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Seattle/Redmond (King County Metro)',
        nearestCityShort: 'Seattle',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wa/seattle/',
    },
    {
        id: 'cascadia-renaissance-faire-wa',
        name: 'Cascadia Renaissance Faire',
        city: 'Randle',
        state: 'WA',
        stateName: 'Washington',
        region: 'Pacific',
        lat: 46.53511,
        lng: -121.95704,
        venue: 'Cowlitz River Rentals',
        address: 'MP 114.5 US-12, Randle, WA 98377',
        url: 'https://cascadiarenfaire.com/',
        datesText: 'Sep 5–6',
        start: '2026-09-05',
        end: '2026-09-06',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Centralia (Amtrak Cascades, FlixBus)',
        nearestCityShort: 'Centralia',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wa/centralia/',
    },
    {
        id: 'spokane-renaissance-faire-wa',
        name: 'Spokane Renaissance Faire',
        city: 'Spokane',
        state: 'WA',
        stateName: 'Washington',
        region: 'Pacific',
        lat: 47.66329,
        lng: -117.34677,
        venue: 'Spokane County Fair & Expo Center',
        address: '404 N Havana St, Spokane, WA 99202',
        url: 'https://spokanerenfaire.com/',
        datesText: 'Oct 2–4',
        start: '2026-10-02',
        end: '2026-10-04',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Spokane (Amtrak Empire Builder, Greyhound, Northwestern Trailways)',
        nearestCityShort: 'Spokane',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wa/spokane/',
    },
    {
        id: 'grayshaven-renaissance-faire-wa',
        name: 'GraysHaven Renaissance Faire',
        city: 'Aberdeen',
        state: 'WA',
        stateName: 'Washington',
        region: 'Pacific',
        lat: 46.97202,
        lng: -123.79789,
        venue: 'GraysHaven grounds',
        address: '500 N Custer St, Aberdeen, WA 98520',
        url: 'https://grayshaven.org/',
        datesText: 'Sep 11–13',
        start: '2026-09-11',
        end: '2026-09-13',
        seasons: ['fall'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Olympia/Centralia (Amtrak + bus)',
        nearestCityShort: 'Olympia',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-wa/olympia/',
    },
    {
        id: 'three-barons-renaissance-fair-ak',
        name: 'Three Barons Renaissance Fair',
        city: 'Anchorage',
        state: 'AK',
        stateName: 'Alaska',
        region: 'Pacific',
        lat: 61.21082,
        lng: -149.78592,
        venue: 'Russian Jack Springs Park',
        address: '801 Pine St, Anchorage, AK 99508',
        url: 'https://3barons.org/',
        datesText: 'Jun 6–7, 13–14',
        start: '2026-06-06',
        end: '2026-06-14',
        seasons: ['summer'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Anchorage (Alaska Railroad, intercity bus)',
        nearestCityShort: 'Anchorage',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ak/anchorage/',
    },
    {
        id: 'end-of-the-road-renaissance-fair-ak',
        name: 'End of the Road Renaissance Fair',
        city: 'Valdez',
        state: 'AK',
        stateName: 'Alaska',
        region: 'Pacific',
        lat: 61.13689,
        lng: -146.35376,
        venue: 'Barney Meyring Park',
        address: '250 Hanagita St, Valdez, AK 99686',
        url: 'https://endoftheroadrenfair.org/',
        datesText: 'Jun 19–20 (free)',
        start: '2026-06-19',
        end: '2026-06-20',
        seasons: ['summer'],
        dateConfidence: 'secondary',
        isFlagship: false,
        nearestCity: 'Anchorage (~305 mi); Valdez shuttle/ferry',
        nearestCityShort: 'Anchorage',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-ak/anchorage/',
    },
    {
        id: 'honokaa-renaissance-faire-hi',
        name: "Honoka'a Renaissance Faire",
        city: "Honoka'a",
        state: 'HI',
        stateName: 'Hawaii',
        region: 'Pacific',
        lat: 20.07556,
        lng: -155.46388,
        venue: 'Māmane St (street fair)',
        address: "45-3574 Māmane St, Honoka'a, HI 96727",
        url: 'https://honokaarenfaire.com/',
        datesText: 'Nov 14 (5th)',
        start: '2026-11-14',
        end: '2026-11-14',
        seasons: ['fall'],
        dateConfidence: 'official',
        isFlagship: false,
        nearestCity: 'Hilo (~40 mi; county bus)',
        nearestCityShort: 'Hilo',
        tripUrl: 'https://www.wanderu.com/en-us/bus-tickets/us-hi/hilo/',
    },
]

/** Every US state code → full name, for the state filter and directory. */
export const stateNames: Record<string, string> = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'Washington DC',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
}

/** Faires we list. Derived — never hardcode a count in copy. */
export const faireCount = faires.length

/** States with at least one faire on the list. Derived, for the same reason. */
export const stateCount = new Set(faires.map((faire) => faire.state)).size

/**
 * Practical notes for the flagship faires, keyed by faire id.
 *
 * Each note is either a character fact or the getting-there catch — never an
 * attendance figure, which reads as a ranking (the brief forbids ranking
 * faires) and tells nobody how to arrive.
 */
export const flagshipNotes: Record<string, string> = {
    'new-york-renaissance-faire-ny':
        'Set in Sterling Forest, an hour up from the city — the Tuxedo train station is the closest rail stop.',
    'pennsylvania-renaissance-faire-pa':
        'Shares its grounds with Mount Hope Estate and its winery. Lancaster is the nearest bus hub.',
    'maryland-renaissance-festival-md':
        'A permanent 16th-century village on 27 wooded acres. Closest to Baltimore, but Annapolis is nearly as near.',
    'carolina-renaissance-festival-nc':
        'A built village north of Charlotte, with a jousting arena at its center.',
    'georgia-renaissance-festival-ga':
        'South of Atlanta off I-85 — the easiest big faire to reach from a major airport.',
    'texas-renaissance-festival-tx':
        'Its own town, Todd Mission, with a campground attached — many people stay the weekend rather than drive back to Houston.',
    'scarborough-renaissance-festival-tx':
        'A spring faire, not a fall one; runs between Dallas and Waxahachie in the Texas heat.',
    'ohio-renaissance-festival-oh':
        'Willy-Nilly-on-the-Wash, a standing village between Cincinnati and Dayton.',
    'michigan-renaissance-festival-mi':
        'North of Detroit near Holly; Flint and Detroit are both reasonable bus approaches.',
    'bristol-renaissance-faire-wi':
        'Right on the Illinois line — closer to Chicago than to Milwaukee, and the Kenosha Metra makes it a train trip.',
    'minnesota-renaissance-festival-mn':
        'Shakopee, southwest of the Twin Cities; runs a shuttle on busy weekends because parking fills.',
    'kansas-city-renaissance-festival-ks':
        'On the Kansas side at Bonner Springs, about 20 miles out from downtown KC.',
    'colorado-renaissance-festival-co':
        'Larkspur sits between Denver and Colorado Springs at 6,700 feet — the altitude is the thing people underestimate.',
    'arizona-renaissance-festival-az':
        'A February-to-April faire east of Phoenix, timed to miss the desert summer entirely.',
    'original-renaissance-pleasure-faire-ca':
        'The 1963 original, and the ancestor of nearly every faire on this page.',
    'northern-california-renaissance-faire-ca':
        'Near Hollister in the Gilroy hills; the Bay Area approach is Caltrain to Gilroy, then a short drive.',
}

/** "Know before you go" tiles. */
export const tips: Tip[] = [
    {
        icon: 'GlassWater',
        text: 'Bring more water than you think you need. Most faires are an open field in late summer, and the shade is a rumor.',
    },
    {
        icon: 'Banknote',
        text: "Carry cash; plenty of the food and drink stalls don't take cards.",
    },
    {
        icon: 'Ticket',
        text: 'Buy tickets ahead for the big ones, which sell out, sometimes weeks out.',
    },
    {
        icon: 'Users',
        text: 'Theme weekends (pirate, Celtic) are the most fun and the most crowded.',
    },
]

/**
 * Query-shaped FAQ; also the source for the page's FAQPage structured data.
 *
 * The "how many" answer states our own count and what it counts, both
 * interpolated from the array, and cites **no national estimate**. Two earlier
 * drafts got this wrong in opposite directions: the handoff claimed "about 225
 * across roughly 45 states" (contradicting the list on both numbers), and the
 * fix then kept 225 as context — which cited a figure we cannot source in order
 * to explain why we differ from it, inviting the comparison rather than settling
 * it. Stating the counting rule is what makes the number defensible; a number to
 * measure ourselves against is not. `data.test.ts` fails if an unsourced
 * national total reappears.
 */
export const faq: FaqItem[] = [
    {
        q: 'When does renaissance faire season start?',
        a: "There isn't one season. The spring circuit runs March through May, summer faires fill June to August, and the biggest wave opens in the fall: New York, Minnesota, and Maryland all start in the back half of August and run into October. Florida and Arizona keep a few going through winter. Labor Day weekend is the one stretch when most of the big fall faires are open at once.",
    },
    {
        q: 'What is the biggest renaissance festival in the US?',
        a: 'By attendance, the Texas Renaissance Festival, at around half a million people over its fall run. Minnesota\'s is the other giant and claims the title too; "largest" is more a rivalry than a settled fact.',
    },
    {
        q: 'What is the oldest renaissance faire in the US?',
        a: 'The Original Renaissance Pleasure Faire near Los Angeles, going since 1963. It more or less invented the American ren faire, and everything else on this list came after it.',
    },
    {
        q: 'How many renaissance faires are there in the US?',
        a: `We list ${faireCount} across ${stateCount} states, running in every season. That counts events rather than organisations — a faire with separate spring and fall editions, or a Yuletide run on the same grounds, is listed once per edition — and it takes the wide definition, so medieval fairs, fantasy festivals and even a Victorian Christmas fair sit alongside the renaissance faires proper. Any on hiatus say so on their entry. Small faires start and fold every year, so the list moves, and we re-verify it each season.`,
    },
    {
        q: 'Are renaissance faires and renaissance festivals the same thing?',
        a: '"Faire", "fair" and "festival" all mean the same event, and which one a given faire uses is just what its founders picked — Texas and Minnesota are festivals, New York and Bristol are faires. This page lists all of them together, however each one spells it.',
    },
    {
        q: 'Can you get to a renaissance faire without a car?',
        a: "As far as the nearby city, usually yes. Most faires sit near a town with bus or train service, and we link the closest one on every listing. The last few miles are the catch: plenty of faires are out on their own grounds past the edge of town, so you finish on the faire's park-and-ride shuttle or a short rideshare.",
    },
    {
        q: 'Do you have to dress up for a renaissance faire?',
        a: "No. Half the crowd's in jeans; the other half has been in full armor since the gates opened. It's all larping, and this is the one place where that's not an insult.",
    },
    {
        q: 'How much do renaissance faire tickets cost?',
        a: "Free at a surprising number of the smaller ones (Alabama's Renaissance Faire, the Medieval Fair of Norman in Oklahoma), and up to about $50 for an adult day pass at the big festivals. Bring cash on top of that: a lot of the food stalls are cash-only.",
    },
]
